
// Data-driven content for PointViews Hjælp-panel. Pure - no DOM - so the
// whole model (ids, cross-references, search) is Level-1 unit testable
// (helpModel.test.js). HelpPanel.js is the only renderer.
//
// One article shape:
//   { id, title, category, summary, keywords:[…],
//     sections:[ { heading?, html } … ], related:[ …ids ] }
//
// Every article `id` is a STABLE public identifier - other UI (a tool's
// info label, a context-menu item, an error message) may later call
// openHelp(id) to jump straight here, so ids are treated as API and must
// not be renamed casually.
//
// The text describes the CURRENT shipped behaviour of the stabilised
// geometry core (top-view intersections, per-feature Z, snap-targets on
// every real vertex, no shared-node scope choice on basic edits). It is
// deliberately short and action-oriented - a reference to read while
// working, not a manual.

import {getKeyboardShortcutGroups, formatShortcutKeys} from "./keyboardShortcuts.js";
import {onLanguageChange, getLanguage, t} from "../i18n/index.js";

// Genvejslisten (fælles kilde med Navigation-modalen) som HTML: én liste pr. gruppe.
function shortcutSections(){
	const sections = [];
	for(const group of getKeyboardShortcutGroups()){
		const rows = group.items.map((item) => `<b>${formatShortcutKeys(item.keys)}</b> - ${item.description}`);
		const list = `<ul>${rows.map((row) => `<li>${row}</li>`).join("")}</ul>`;
		sections.push({heading: group.title, html: group.note ? list + `<p>${group.note}</p>` : list});
	}
	return sections;
}

const P = "#2f8fd6";      // PointView line blue
const SEL = "#ffa500";    // selection orange
const ACCENT = "#007FFF"; // accent

// Small inline diagrams. Theme-safe: explicit colours, transparent
// background, scale with font-size via width/height in em.
const DIAGRAMS = {
	get trimLine(){ return `<svg viewBox="0 0 120 60" width="120" height="60" role="img" aria-label="${t("Trim: linjen afkortes ved skæringen")}">
		<line x1="6" y1="24" x2="70" y2="24" stroke="${P}" stroke-width="2"/>
		<line x1="46" y1="6" x2="46" y2="54" stroke="${SEL}" stroke-width="2"/>
		<circle cx="46" cy="24" r="3.5" fill="${P}"/>
		<line x1="70" y1="24" x2="114" y2="24" stroke="${P}" stroke-width="1" stroke-dasharray="3 3" opacity="0.5"/>
	</svg>`; },
	get extend(){ return `<svg viewBox="0 0 120 60" width="120" height="60" role="img" aria-label="${t("Forlæng: linjen forlænges til referencen")}">
		<line x1="6" y1="24" x2="70" y2="24" stroke="${P}" stroke-width="2" stroke-dasharray="3 3" opacity="0.5"/>
		<line x1="6" y1="24" x2="46" y2="24" stroke="${P}" stroke-width="2"/>
		<line x1="70" y1="6" x2="70" y2="54" stroke="${SEL}" stroke-width="2"/>
		<circle cx="70" cy="24" r="3.5" fill="${P}"/>
	</svg>`; },
	get split(){ return `<svg viewBox="0 0 120 40" width="120" height="40" role="img" aria-label="${t("Split: én linje bliver til to")}">
		<line x1="6" y1="20" x2="58" y2="20" stroke="${P}" stroke-width="2"/>
		<line x1="62" y1="20" x2="114" y2="20" stroke="${SEL}" stroke-width="2"/>
		<circle cx="60" cy="20" r="3.5" fill="#fff"/>
	</svg>`; },
	get intersection(){ return `<svg viewBox="0 0 120 60" width="120" height="60" role="img" aria-label="${t("Sæt punkt ved skæring")}">
		<line x1="6" y1="30" x2="114" y2="30" stroke="${P}" stroke-width="2"/>
		<line x1="60" y1="6" x2="60" y2="54" stroke="${SEL}" stroke-width="2"/>
		<circle cx="60" cy="30" r="3.5" fill="#fff" stroke="${P}" stroke-width="1.5"/>
	</svg>`; },
	get midpoint(){ return `<svg viewBox="0 0 120 40" width="120" height="40" role="img" aria-label="${t("Midtpunkt mellem to punkter")}">
		<line x1="12" y1="20" x2="108" y2="20" stroke="${P}" stroke-width="1" stroke-dasharray="3 3" opacity="0.5"/>
		<circle cx="12" cy="20" r="3.5" fill="${P}"/>
		<circle cx="108" cy="20" r="3.5" fill="${P}"/>
		<circle cx="60" cy="20" r="4" fill="none" stroke="${SEL}" stroke-width="2"/>
	</svg>`; },
	get perpendicular(){ return `<svg viewBox="0 0 120 60" width="120" height="60" role="img" aria-label="${t("Vinkelret på basislinjen")}">
		<line x1="10" y1="44" x2="110" y2="44" stroke="${P}" stroke-width="2"/>
		<line x1="60" y1="44" x2="60" y2="10" stroke="${SEL}" stroke-width="2"/>
		<rect x="53" y="37" width="7" height="7" fill="none" stroke="${SEL}" stroke-width="1"/>
	</svg>`; },
	get parallel(){ return `<svg viewBox="0 0 120 44" width="120" height="44" role="img" aria-label="${t("Parallel linje i konstant afstand")}">
		<line x1="8" y1="14" x2="112" y2="14" stroke="${P}" stroke-width="2"/>
		<line x1="8" y1="32" x2="112" y2="32" stroke="${SEL}" stroke-width="2"/>
	</svg>`; },
};

// Kategorier og artikler bygges af funktioner (teksterne slås op på det aktive sprog), og bygges om, når sproget skifter
// (arrayerne ændres på stedet, så alle, der har importeret dem, ser den nye tekst).
function buildCategories(){
	return [
	{id: "start", label: t("Kom hurtigt i gang")},
	{id: "registrering", label: t("Registrering")},
	{id: "konstruktion", label: t("Konstruktion og beregnet geometri")},
	{id: "special", label: t("Specialiseret registrering")},
	{id: "maalinger", label: t("Målinger")},
	{id: "redigering", label: t("Redigering")},
	];
}
export const HELP_CATEGORIES = buildCategories();

const S = (heading, html) => ({heading, html});
const step = (items) => `<ol>${items.map((t) => `<li>${t}</li>`).join("")}</ol>`;
const ul = (items) => `<ul>${items.map((t) => `<li>${t}</li>`).join("")}</ul>`;

function buildArticles(){
	return [
	// ─────────────────────────── KOM HURTIGT I GANG ───────────────────────
	{
		id: "basics",
		title: t("Navigation, valg og snapping"),
		category: "start",
		summary: t("Det grundlæggende: se dig omkring, aktivér et værktøj, vælg geometri og afbryd."),
		keywords: [t("navigation"), t("kom i gang"), t("grundlæggende"), t("shift"), t("højreklik"), t("esc"), t("annuller"), t("vælg"), t("kamera")],
		sections: [
			S(t("Se dig omkring"), t("Sådan du bevæger dig afhænger af navigationstypen (<b>Jord-navigation</b> eller <b>Kreds-navigation</b>) - vælg den og se den præcise betjening under <b>Indstillinger → Navigation</b> i værktøjslinjen (se emnet <i>Navigation</i>). Rul zoomer i begge. Hold <b>Ctrl</b> nede for at navigere frit uden at et aktivt værktøj reagerer på klikkene.")),
			S(t("Aktivér et værktøj"), t("Åbn en af værktøjsgrupperne i værktøjslinjen (<b>Registrering</b>, <b>Måling</b>, <b>Analysevisninger</b> eller <b>Rapporter</b>) og vælg værktøjet - se emnet <i>Værktøjsgrupper</i>. Værktøjet forbliver aktivt, til du afslutter det (se <i>Afbryd</i> nedenfor) eller vælger et andet. Venstreklik placerer punkter; hvor det er relevant afslutter du et forløb med højreklik.")),
			S(t("Den blå kugle ved cursoren"), t("Er et værktøj aktivt, viser en lille <b>blå kugle</b> ved cursoren det punkt i punktskyen, et venstreklik vil placere - også når snapping flytter punktet til et eksisterende punkt. Den vises for alle Registrerings- og Måle-værktøjer.")),
			S(t("Streg til cursoren"), t("Når du har sat det første punkt, følger en stiplet <b>streg</b> cursoren fra det sidst placerede punkt, så du ser, hvor næste punkt lander (også når snapping flytter det). Ved en polygon vises også lukkelinjen tilbage til det første punkt. Stregen forsvinder, når forløbet er færdigt eller afbrudt.")),
			S(t("Vælg en registrering"), t("Venstreklik på en registreret geometri for at markere den - den bliver orange. Markeringen bruges bl.a. når du vil redigere hele objektet.")),
			S(t("Redigér med Shift + højreklik"), t("Hold <b>Shift</b> og højreklik direkte på et punkt, et segment eller geometrien for at åbne en menu med de handlinger, der passer til netop det, du pegede på. Se <i>Vælg punkt, segment eller hel feature</i>.")),
			S(t("Afbryd"), t("<b>ESC</b> annullerer den igangværende handling - et påbegyndt forløb, en preview eller et referencevalg - uden at ændre allerede gemt geometri.")),
			S(t("Afslut værktøjet med højreklik"), t("Er et værktøj aktivt, men du <i>ikke</i> er begyndt på noget (intet punkt placeret, ingen dialog åben), afslutter et almindeligt <b>højreklik</b> værktøjet - ligesom ESC. Højreklik midt i et forløb afslutter eller annullerer kun forløbet, som beskrevet i det enkelte værktøj. <b>Shift</b>+højreklik (redigeringsmenuen), <b>Ctrl</b>+højreklik og højre museknap + træk (flyt visningen) påvirker ikke værktøjet.")),
		],
		related: ["navigation", "toolbar-groups", "selection", "snap", "point", "line"],
	},
	{
		id: "toolbar-groups",
		title: t("Værktøjsgrupper"),
		category: "start",
		summary: t("Alle værktøjer findes i fire faglige grupper i værktøjslinjen."),
		keywords: [t("værktøjsgrupper"), t("værktøjslinje"), t("topbar"), t("registrering"), t("måling"), t("analysevisninger"), t("rapporter"), t("værktøjer"), t("menu")],
		sections: [
			S(t("De fire grupper"), ul([
				t("<b>Registrering</b> - opret objekter, der repræsenterer noget i punktskyen: Punkt, Linje, Polygon, Cirkel, Rektangel, Bue, Farvet areal, Midtpunkt, Midterlinje, Parallelforskydning, Vinkelret linje, Vinkelret punkt og Find rør."),
				t("<b>Måling</b> - bestem værdier og dimensioner: Mål punkt, afstand, højde, areal, vinkel, cirkel og azimut, Mål areal og volumen samt Mål rørdimension og koter. Er en IFC/OBJ-model indlæst, findes her også 3-akset laser, Punkt til nærmeste flade og Flade til punkt."),
				t("<b>Analysevisninger</b> - undersøg og afgræns punktskyen: 2D-profil og Snit (clip, snitplan og afvigelsesanalyse)."),
				t("<b>Rapporter</b> - generér dokumentation: Tværprofilrapport og Graverapport."),
			])),
			S(t("Sådan bruger du dem"), t("Klik gruppens ikon i værktøjslinjen (til højre, lige før de øvrige ikoner) og vælg et værktøj i menuen. Hold musen over et værktøj for at se en længere beskrivelse af, hvad det gør.")),
			S(t("Registreringer og Målinger"), t("Registrerings-værktøjerne giver objekter under <b>Registreringer</b> i Registreringer-panelet, og måle-værktøjerne giver objekter under <b>Målinger</b>. Det samme gælder 2D-profiler og Clip-objekter fra Analysevisninger: de lægges under <b>Målinger</b>, hvor de kan skjules, omdøbes, åbnes igen og slettes. Kun registreringer kan gemmes/eksporteres - se emnet <i>Registreringer-panelet</i>.")),
			S(t("Data & Lag"), t("Øverst i Registreringer-panelet ligger gruppen <b>Data & Lag</b> med de lag, du har tændt: <b>3D-baggrundskortet</b>, den indlæste <b>IFC/OBJ-model</b> og dine <b>GIS-lag</b>. GIS-lagene ligger samlet i undergrupper efter kilde: <b>LER</b> (med ledningsejerne som rækker), <b>Lokale GIS-filer</b>, <b>WFS</b> og <b>OGC API – Features</b>. Her tændes og slukkes de med øjet (en gruppes øje tænder/slukker alle i den), og papirkurven fjerner et lag - eller en hel gruppe, efter en bekræftelse. De samme lag styres stadig fra Lag, IFC / Model og GIS - de to steder følger hinanden. Lag er ikke registreringer og kommer aldrig med i Gem registreringer.")),
			S("PointView 360", t("I et PointView 360-projekt er kun panoramaet indlæst fra start. Aktiverer du et registrerings-, måle- eller analyseværktøj, indlæses punktskyen automatisk først (en kort besked vises), og værktøjet starter, når den er klar - værktøjerne virker kun på en indlæst punktsky.")),
		],
		related: ["basics", "measure-distance", "cross-section"],
	},
	{
		id: "navigation",
		title: t("Navigation"),
		category: "start",
		summary: t("Vælg navigationstype og kamera-projektion, og se betjeningen af den aktive navigation."),
		keywords: [t("navigation"), t("naviger"), t("jord-navigation"), t("kreds-navigation"), t("kamera"), t("projektion"), t("perspektiv"), t("ortografisk"), t("standardvisning"), t("kompas"), t("zoom"), t("panorér"), t("rotér"), t("mus"), t("tastatur")],
		sections: [
			S(t("Sådan åbner du den"), t("Klik på <b>Indstillinger</b> (tandhjuls-ikonet) i værktøjslinjen og vælg <b>Navigation</b>. Modalen samler navigationstype, kamera-projektion, standardvisninger og en betjeningsvejledning.")),
			S(t("Navigationstype"), ul([
				t("<b>Jord-navigation</b> - venstre museknap + træk flytter dig langs det vandrette plan, højre museknap + træk drejer blikket om punktet under musen. Piletaster flytter dig, <b>Alt</b> + ↑/↓ går op/ned, og <b>Shift</b> + piletaster drejer blikket. Rul zoomer mod punktet under musen."),
				t("<b>Kreds-navigation</b> - venstre museknap + træk drejer blikket omkring målpunktet, højre museknap + træk flytter visningen. Rul zoomer. Ingen tastaturbetjening."),
				t("I begge zoomer et <b>dobbeltklik</b> til det punkt, du klikker på."),
			])),
			S(t("Kamera-projektion"), t("<b>Perspektiv</b> (standard) eller <b>Ortografisk</b>.")),
			S(t("Standardvisninger"), t("De seks knapper vender kameraet mod venstre, højre, forfra, bagfra, top eller bund og zoomer samtidig til hele punktskyens udstrækning. Kompas-kuben i vieweren bevarer afstanden: klik på en flade for at se fra den retning, eller tag fat i kuben og træk for at dreje punktskyen frit; højreklik på kuben åbner en menu med bl.a. Isometrisk.")),
			S(t("Betjening"), t("Tabellen i modalen følger den valgte navigationstype og viser, hvordan musen og tasterne bruges.")),
		],
		related: ["basics", "toolbar-groups", "keyboard-shortcuts"],
	},
	{
		id: "keyboard-shortcuts",
		title: t("Keyboard shortcuts"),
		category: "start",
		summary: t("Alle tastaturgenveje i PointView samlet ét sted - også at finde i Navigation-modalen."),
		keywords: [t("keyboard"), t("shortcuts"), t("tastatur"), t("genvej"), t("genveje"), t("taster"), t("tastaturgenveje"), t("hurtigtast"), t("esc"), t("ctrl"), t("shift"), t("piletaster"), t("kopier"), t("label")],
		sections: [
			S(t("Overblik"), t("Her står alle genveje, der virker i vieweren. Genvejene virker ikke, mens du skriver i et tekst- eller talfelt. Den samme liste vises som en ekstra kolonne i <b>Navigation</b>-modalen (Indstillinger → Navigation).")),
			...shortcutSections(),
		],
		related: ["basics", "navigation", "classification-shortcuts", "label-shortcuts", "copy-coordinates-shortcut", "top-view-shortcut", "snap"],
	},
	{
		id: "selection",
		title: t("Vælg punkt, segment eller hel feature"),
		category: "start",
		summary: t("Redigering starter med Shift + højreklik. Hvad du peger på afgør, hvilke handlinger menuen viser."),
		keywords: [t("shift"), t("højreklik"), t("kontekstmenu"), t("menu"), t("vælg"), t("punkt"), t("segment"), t("linjepunkt"), t("endepunkt"), t("vertex"), t("hel feature"), t("objekt")],
		sections: [
			S(t("Et punkt"), t("Hold musen over et registreret punkt, så det fremhæves, og Shift + højreklik på det. Menuen hedder <b>Linjepunkt</b> for et punkt inde på en linje, <b>Endepunkt</b> for en åben linjes første eller sidste punkt, og <b>Punkt</b> for et selvstændigt punkt-objekt.")),
			S(t("Et segment"), t("Shift + højreklik på linjestykket <i>mellem</i> to punkter, uden at ramme et punkt. Menuen hedder <b>Segment</b>.")),
			S(t("En del af en linje"), t("Fra segment-menuen: <b>Vælg sammenhængende del…</b>, og klik derefter linjens start- og slutpunkt for det stykke, du vil arbejde med.")),
			S(t("Hele objektet"), t("Shift + højreklik på en polygons flade, på et Rør, eller vælg <b>Vælg hele objektet</b> fra segment-menuen. Menuen hedder <b>Linje</b> / <b>Polygon</b> / <b>Tværprofil</b> / <b>Rør</b>.")),
			S(t("Flere punkter ad gangen"), t("Vælg flere punkter med et rektangel (<b>Ctrl</b> + højreklik + træk), og Shift + højreklik. Menuen viser <b>Ryd valgte</b> og <b>Slet valgte punkter</b>. Højreklikker du <i>på et af punkterne</i>, får du også <b>Snap til dette punkt</b>: alle valgte punkter flyttes til det punkts præcise koordinater (X, Y og Z). Punktet, du højreklikkede på, flyttes ikke. Objekter bundet til en integration (låst geometri) springes over.")),
			S(t("Vigtigt"), t("Menuen viser kun handlinger, der giver mening for det, du valgte. Handlinger, der ikke passer, vises slet ikke - de er ikke grået ud.")),
		],
		related: ["basics", "edit-point", "edit-segment", "edit-line", "edit-line-part"],
	},
	{
		id: "settings-sidepanel",
		title: t("Indstillinger"),
		category: "start",
		summary: t("Indstillinger-menuen (tandhjul) i værktøjslinjen samler navigation, udseende, opsætning, deling og Digitaliserings egenskaber. Klassifikation, Lag, GIS og IFC / Model ligger i knappen Data & Lag."),
		keywords: [t("indstillinger"), t("settings"), t("snap"), t("lag"), t("matrikelkort"), t("veje"), t("gem opsætning"), t("indlæs opsætning"), t("del"), t("quickview"), t("overfladedetektion"), t("digitalisering-menu"), t("maksimal pilhøjde"), t("vinkeltolerance"), t("standardafstand"), t("tolerance")],
		sections: [
			S(t("Indstillinger-menuen"), ul([
				t("<b>Navigation</b> - navigationstype, kamera-projektion, standardvisninger og betjeningsvejledning."),
				t("<b>Data & Lag</b> er en egen knap i værktøjslinjen (ikonet med tre stablede lag) og samler <b>Klassifikation</b> (vis eller skjul de klasser, der findes i de indlæste punktskyer), <b>Lag</b> (Matrikelkort og Veje oven på punktskyen, kun i topvisning, med højde-forskydning, og <b>GCP'er</b> som punkter med navn), <b>GIS</b> (GIS-lag, fx LER) og <b>IFC / Model</b> (IFC/OBJ-modeller) - se emnerne <i>Klassifikation</i>, <i>GIS</i> og <i>IFC / Model</i>."),
				t("<b>Udseende</b> - alle visuelle indstillinger: punktbudget, mindste gitterstørrelse, høj splat-kvalitet, skyggeeffekt (til/fra, radius, styrke og punktskyens gennemsigtighed), synsfelt, lås visning, baggrund og bounding box - samt mesh (vis/skjul og gennemsigtighed) og, hvis der findes tegninger i sidepanelet, deres gennemsigtighed. Gemmes i din browser."),
				t("<b>Egenskaber</b> - detaljerede indstillinger (se nedenfor)."),
				t("<b>Del (QuickView)</b> - opretter et link til den aktuelle visning med en udløbsdato og viser linket med en kopiér-knap."),
				t("<b>Hjælp</b> har sit eget ikon (spørgsmålstegn) i værktøjslinjen, og <b>Koordinatsystem</b> findes under modellens <b>Modelinformation</b> (info-ikonet ud for modellen)."),
			])),
			S(t("Modeller (Model-pillen)"), ul([
				t("<b>Afkrydsning</b> - modellen er indlæst/vist. Fjern afkrydsningen for at skjule den; sæt den igen for at vise den."),
				t("<b>Aktiv model</b> (blå række og mærket <i>Aktiv</i>) - nye registreringer knyttes til den. Klik på en indlæst model for at gøre den aktiv og zoome til den. En model, der ikke er indlæst, kan ikke være aktiv; skjules den aktive, overtager den første indlæste model."),
				t("<b>Info</b> viser Modelinformation, og <b>Punktindstillinger</b> (justér-ikonet) åbner punktstørrelse, farvetype m.m. for netop den model (kun for en indlæst model)."),
				t("<b>Genberegn</b> vises kun på de modeller, hvor genberegning er mulig."),
				t("<b>Fjern fra visning</b> (skraldespanden) fjerner modellen fra den aktuelle visning. Modellen og dens data slettes ikke. Den sidste model kan ikke fjernes."),
			])),
			S(t("Egenskaber"), t("Vælg <b>Egenskaber</b> i Indstillinger-menuen. Ændringerne gemmes i din browser og huskes næste gang. <b>Nulstil egenskaber</b> under Generelt sætter alt tilbage til standard.")),
			S(t("Interaktion"), ul([
				t("<b>Snap</b> - ÉN kontakt, der slår automatisk snap til/fra i hele PointView: Registrering, Måling og tegningerne i sidepanelet (standard: til). De eksplicitte handlinger <i>Snap til punktsky</i> og <i>Snap til andet punkt</i> virker altid."),
				t("<b>Snap-afstand</b> (standard 15 px, 3-50) - hvor tæt musen skal være på et eksisterende punkt, målt i skærm-pixels. Gælder Registrering og Måling; tegningerne i sidepanelet bruger en fast afstand i meter og følger kun til/fra-kontakten."),
				t("<b>Overfladedetektion</b> - hjælper med at placere målinger og registreringer på den forreste overflade i punktskyen og reducerer risikoen for, at der placeres på punkter bag den overflade, du arbejder på (standard: fra). En lille cirkel med fast størrelse på skærmen viser den fundne overflade og dens retning, og den er dæmpet, når overfladen ikke kan bestemmes sikkert (kant, rør eller for få punkter)."),
			])),
			S(t("Opsætning"), ul([
				t("<b>Gem opsætning</b> / <b>Indlæs opsætning</b> - gemmer/indlæser visningens opsætning (valgte punktskyer, kamera, visningsindstillinger m.m.) som en settings.json-fil. Det er <b>ikke</b> en gemning af Registreringer."),
			])),
			S(t("Vinkelret / parallel"), ul([
				t("<b>Standardafstand</b> - forudfylder afstandsdialogen ved Vinkelret punkt/linje og Parallelforskydning. 0 betyder ingen forudfyldt værdi."),
				t("<b>Vis hjælpelinjer</b>, <b>Vis afstand</b>, <b>Vis projektionspunkt</b> og <b>Vis basislinje under registrering</b> - påvirker kun visningen under registrering, ikke resultatet."),
			])),
			S(t("Vektorredigering"), ul([
				t("<b>Maksimal pilhøjde</b> (standard 0,02 m) - hvor langt et punkt højst må flyttes eller fjernes fra den oprindelige geometri ved automatisk udretning og forenkling."),
				t("<b>Vinkeltolerance</b> (standard 5°) - hvor langt en vinkel må afvige fra 90° eller 180°, før den medtages i ortogonaliseringen."),
			])),
			S(t("Topologi"), t("<b>Plan-tolerance (XY)</b> - hvor tæt to geometrier skal være i plan for at tælle som en reel skæring/forbindelse ved Forlæng, Trim, Sæt punkt ved skæring, Saml linjer og Luk gap. Her ligger også knappen <b>Kør tjek</b>, som starter Topologi-tjek (se det emne).")),
			S(t("Vigtigt"), t("En operations EGEN parameter (fx Pilhøjde, Radius, Længde) indtastes altid direkte i den pågældende dialog - aldrig kun i Indstillinger. Indstillinger holder kun globale tolerancer og standardværdier, aldrig det aktuelle resultat af en igangværende handling.")),
		],
		related: ["basics", "arc-from-sagitta"],
	},
	{
		id: "classification-shortcuts",
		title: t("Hurtig klassifikationsvisning (1-4)"),
		category: "start",
		summary: t("Taltasterne 1-4 tænder/slukker hurtigt for de fire hyppigste klassifikationer under registrering."),
		keywords: [t("klassifikation"), t("classification"), t("støj"), t("terræn"), t("anlægsgrav"), t("ledninger"), t("1"), t("2"), t("3"), t("4"), t("vis skjul")],
		sections: [
			S(t("Klasserne"), ul([
				t("<b>1</b> - Støj (low point/noise)"),
				t("<b>2</b> - Terræn (ground)"),
				t("<b>3</b> - Anlægsgrav (building)"),
				t("<b>4</b> - Ledninger (water)"),
			])),
			S(t("Sådan bruges de"), t("Tryk taltasten for at tænde/slukke den pågældende klasse. Tryk igen for at vise den igen.")),
			S(t("Samme data som Klassifikation"), t("Taltasterne er blot en hurtig genvej til de samme klasser, som du styrer under <b>Data & Lag → Klassifikation</b> - de viser og ændrer nøjagtig samme tilstand, så de altid er enige.")),
			S(t("Vigtigt"), t("Taltasterne 1-4 virker ikke, mens du skriver i et tal- eller tekstfelt (fx i en geometri-popup) - så de aldrig utilsigtet slukker for en klasse, mens du indtaster fx en radius.")),
		],
		related: ["basics", "label-shortcuts"],
	},

	// ─────────────────────────────── REGISTRERING ─────────────────────────
	{
		id: "point",
		title: t("Registrér et punkt"),
		category: "registrering",
		summary: t("Sæt et enkelt 3D-punkt på en position i punktskyen."),
		keywords: [t("punkt"), t("point"), t("digitalisér"), t("registrer"), t("vertex")],
		sections: [
			S(t("Sådan gør du"), step([
				t("Aktivér <b>Punkt</b>."),
				t("Venstreklik på den ønskede position i punktskyen. Punktet gemmes med det samme."),
				t("Værktøjet er stadig aktivt - klik videre for at sætte flere punkter."),
			])),
			S(t("Resultat"), t("Ét selvstændigt punkt-objekt pr. klik. Punktet kan efterfølgende flyttes, snappes og bruges som fikspunkt for andre linjer.")),
			S(t("Tip"), t("Placeres punktet tæt på et eksisterende registreret punkt, snapper det oveni. Se <i>Snapping</i>.")),
		],
		related: ["snap", "line", "edit-point"],
	},
	{
		id: "line",
		title: t("Registrér en linje"),
		category: "registrering",
		summary: t("Tegn en 3D-linje gennem to eller flere punkter i punktskyen."),
		keywords: [t("linje"), t("line"), t("polyline"), t("digitalisér"), t("registrer"), t("vektor")],
		sections: [
			S(t("Sådan gør du"), step([
				t("Aktivér <b>Linje</b>."),
				t("Venstreklik for hvert punkt langs forløbet."),
				t("Højreklik for at afslutte (mindst to punkter)."),
			])),
			S(t("Resultat"), t("Én linje. Linjen har en retning - fra det første til det sidste punkt - som vises med små pile langs linjen. Se <i>Linjeretning</i>.")),
			S(t("Tip"), t("Under tegning snapper et nyt punkt til et hvilket som helst eksisterende registreret punkt, det placeres tæt på.")),
		],
		related: ["snap", "reverse-direction", "edit-line", "edit-segment"],
	},
	{
		id: "polygon",
		title: t("Registrér et polygon"),
		category: "registrering",
		summary: t("Tegn en lukket 3D-figur gennem tre eller flere punkter."),
		keywords: [t("polygon"), t("flade"), t("areal"), t("lukket"), t("digitalisér"), t("registrer")],
		sections: [
			S(t("Sådan gør du"), step([
				t("Aktivér <b>Polygon</b>."),
				t("Venstreklik for hvert hjørne."),
				t("Højreklik for at afslutte (mindst tre hjørner). Figuren lukkes automatisk."),
			])),
			S(t("Resultat"), t("Ét lukket polygon med udfyldt flade.")),
			S(t("Vigtigt"), t("For et polygon tilbyder redigeringsmenuen kun de handlinger, der er sikre for en lukket figur - typisk Forenkle, Ortogonaliser, Snap alle punkter og Slet objekt.")),
		],
		related: ["edit-polygon", "measure-area", "area-volume", "circle", "rectangle"],
	},
	{
		id: "circle",
		title: t("Cirkel"),
		category: "registrering",
		summary: t("Digitalisér en ægte 3D-cirkel: klik for centrum, flyt musen for radius - punktskyen estimerer selv cirklens plan."),
		keywords: [t("cirkel"), t("circle"), t("radius"), t("diameter"), t("rediger cirkel"), t("opløs cirkel"), t("periferi")],
		sections: [
			S(t("Sådan gør du"), step([
				t("Aktivér <b>Cirkel</b> og venstreklik for at placere cirklens <b>centrum</b>."),
				t("Flyt musen for at angive <b>radius</b> (vises live) og venstreklik for at afslutte."),
				t("En popup åbner med Radius og Diameter i cm (synkroniserede). Skriv en værdi - cirklen opdateres med det samme."),
				t("Klik <b>Godkend</b> for at gemme, eller <b>Annuller</b>/ESC for at springe over uden at oprette noget."),
			])),
			S(t("3D og antal punkter"), t("Cirklens plan estimeres løbende ud fra punktskyen omkring centrum, mens du flytter musen - den er ikke låst til vandret. Punktskyen orienterer kun cirklen; selve ringen er altid en eksakt cirkel. Antallet af punkter tilpasses automatisk efter størrelsen; center og plan ændres ikke, når du justerer radius.")),
			S(t("Senere redigering"), t("Shift + højreklik på cirklen → <b>Rediger cirkel…</b> (samme popup, live) eller <b>Opløs cirkel</b> (fjerner kun den matematiske definition - punkterne beholder deres nuværende position).")),
		],
		related: ["rectangle", "arc-direct", "polygon", "measure-circle"],
	},
	{
		id: "rectangle",
		title: t("Rektangel"),
		category: "registrering",
		summary: t("Digitalisér et matematisk rektangel (90° hjørner, parallelle sider) ud fra tre punkter."),
		keywords: [t("rektangel"), t("rectangle"), t("firkant"), t("længde"), t("bredde"), t("vend side"), t("rediger rektangel"), t("opløs rektangel")],
		sections: [
			S(t("Sådan gør du"), step([
				t("Aktivér <b>Rektangel</b>."),
				t("Klik 1: rektanglets første hjørne."),
				t("Klik 2: angiver siderretning og en første længde."),
				t("Klik 3: angiver bredden og det lokale 3D-plan."),
				t("Juster Længde/Bredde i popup'en - ændringen vises med det samme. <b>Vend side</b> flytter rektanglet til modsatte side af basislinjen."),
			])),
			S(t("Stabil reference"), t("Det første hjørne og retningen fra første til andet klik holdes fast, mens du justerer længde og bredde.")),
			S(t("Senere redigering"), t("Shift + højreklik på rektanglet → <b>Rediger rektangel…</b> eller <b>Opløs rektangel</b> (bevarer de nuværende koordinater).")),
		],
		related: ["circle", "arc-direct", "polygon"],
	},
	{
		id: "arc-direct",
		title: t("Bue (direkte oprettelse)"),
		category: "registrering",
		summary: t("Digitalisér en bue direkte med tre klik, i stedet for at lave en lige linje først."),
		keywords: [t("bue"), t("arc"), t("direkte"), t("start"), t("slut"), t("plan"), t("side")],
		sections: [
			S(t("Sådan gør du"), step([
				t("Aktivér <b>Bue</b>."),
				t("Klik 1: buens startpunkt. Klik 2: buens slutpunkt. Klik 3: et punkt der viser buens plan og side."),
				t("Samme popup som <i>Bue fra pilhøjde</i> åbner: skriv en pilhøjde i cm - buen opdateres live."),
			])),
			S(t("Hvorfor tre klik?"), t("To punkter fastlægger kun en kord, ikke et 3D-plan - der findes uendeligt mange buer gennem to punkter. Det tredje klik gør valget entydigt og stabilt, uafhængigt af kameravinkel.")),
			S(t("Samme buematematik som Bue fra pilhøjde"), t("Denne genvej opretter blot en ny linje og springer direkte til den velkendte pilhøjde-dialog - fortegn, live preview, automatisk punktantal og efterfølgende Rediger/Vend/Opløs virker identisk. Se <i>Bue fra pilhøjde</i> for detaljerne.")),
		],
		related: ["arc-from-sagitta", "circle", "rectangle"],
	},

	// ─────────────────────── KONSTRUKTION / BEREGNET ──────────────────────
	{
		id: "midpoint",
		title: t("Midtpunkt"),
		category: "konstruktion",
		summary: t("Beregn midtpunktet mellem to valgte punkter og gem det som et selvstændigt punkt."),
		keywords: [t("midtpunkt"), t("midt"), t("mellem"), t("center"), t("beregnet")],
		sections: [
			S("", DIAGRAMS.midpoint),
			S(t("Sådan gør du"), step([
				t("Aktivér <b>Midtpunkt</b>."),
				t("Venstreklik på det første punkt, derefter det andet."),
				t("Midtpunktet beregnes og gemmes. Hjælpepunkterne og hjælpelinjen forsvinder igen."),
			])),
			S(t("Resultat"), t("Ét punkt midt mellem de to valgte positioner - et rigtigt registreret punkt, som andre linjer kan snappe til.")),
		],
		related: ["point", "snap", "midline"],
	},
	{
		id: "midline",
		title: t("Midterlinje"),
		category: "konstruktion",
		summary: t("Konstruér en linje midt mellem to eksisterende linjer."),
		keywords: [t("midterlinje"), t("midtlinje"), t("center"), t("mellem to linjer"), t("beregnet")],
		sections: [
			S(t("Sådan gør du"), step([
				t("Aktivér <b>Midterlinje</b>."),
				t("Digitalisér den første hjælpevektor (venstreklik for hvert punkt, højreklik afslutter)."),
				t("Digitalisér den anden hjælpevektor på samme måde."),
				t("Midterlinjen dannes automatisk midt mellem de to og gemmes som en almindelig linje."),
			])),
			S(t("Resultat"), t("Én linje midtvejs mellem de to hjælpevektorer. Hjælpevektorerne fjernes.")),
			S(t("Tip"), t("Midterlinjen kan bagefter redigeres som enhver anden linje.")),
		],
		related: ["midpoint", "line", "edit-line"],
	},
	{
		id: "perpendicular-point",
		title: t("Vinkelret punkt"),
		category: "konstruktion",
		summary: t("Registrér et punkt, der ligger præcist vinkelret ud fra en basislinje."),
		keywords: [t("vinkelret"), t("perpendikulær"), t("normal"), t("basislinje"), t("90 grader"), t("offset")],
		sections: [
			S("", DIAGRAMS.perpendicular),
			S(t("Sådan gør du"), step([
				t("Aktivér <b>Vinkelret punkt</b>."),
				t("Digitalisér først en basislinje."),
				t("Bevæg musen: punktet låses til den vinkelrette linje ud fra det nærmeste basislinjesegment. Venstreklik for at placere det."),
			])),
			S(t("Resultat"), t("Ét punkt, hvis afstand til basislinjen er målt vinkelret på den. Punktets højde er den, du peger på i punktskyen.")),
		],
		related: ["perpendicular-line", "parallel-offset", "parallel-perpendicular"],
	},
	{
		id: "perpendicular-line",
		title: t("Vinkelret linje"),
		category: "konstruktion",
		summary: t("Registrér flere punkter vinkelret på en basislinje og forbind dem til en linje."),
		keywords: [t("vinkelret"), t("perpendikulær"), t("basislinje"), t("linje"), t("tværsnit")],
		sections: [
			S(t("Sådan gør du"), step([
				t("Aktivér <b>Vinkelret linje</b>."),
				t("Digitalisér først en basislinje."),
				t("Venstreklik for hvert punkt vinkelret på basislinjen."),
				t("Højreklik for at afslutte."),
			])),
			S(t("Resultat"), t("Én linje gennem de vinkelret registrerede punkter.")),
		],
		related: ["perpendicular-point", "parallel-offset"],
	},
	{
		id: "parallel-offset",
		title: t("Parallelforskydning"),
		category: "konstruktion",
		summary: t("Opret en linje parallelt med en basislinje i en konstant afstand."),
		keywords: [t("parallel"), t("parallelforskydning"), t("offset"), t("afstand"), t("forskyd"), t("basislinje")],
		sections: [
			S("", DIAGRAMS.parallel),
			S(t("Sådan gør du"), step([
				t("Aktivér <b>Parallelforskydning</b>."),
				t("Digitalisér en basislinje."),
				t("Bevæg musen til den ønskede side og afstand - den parallelle linje forhåndsvises. Venstreklik for at gemme den."),
			])),
			S(t("Resultat"), t("Én linje med konstant planafstand til hver af basislinjens segmenter. Hvert punkt beholder højden fra det tilsvarende basislinjepunkt.")),
			S(t("Vigtigt"), t("Ved meget spidse hjørner på basislinjen afkortes den parallelle linjes hjørne (som en fase), så den ikke danner en urimelig lang spids.")),
		],
		related: ["perpendicular-line", "parallel-perpendicular", "line"],
	},
	{
		id: "area-volume",
		title: t("Mål areal og volumen"),
		category: "special",
		summary: t("Beregn areal, påfyldning og udgravning for et område ud fra punktskyens overflade."),
		keywords: [t("mål areal og volumen"), t("areal"), t("volumen"), t("volume"), t("påfyldning"), t("udgravning"), t("tin"), t("overflade"), t("beregning"), t("referenceplan"), t("kubik"), t("m3")],
		sections: [
			S(t("Sådan gør du"), step([
				t("Aktivér <b>Mål areal og volumen</b> under <b>Måling</b> i værktøjslinjen."),
				t("Digitalisér polygonen <b>langs den fysiske rand</b> omkring området (fx kanten af en udgravning eller et depot). Venstreklik tilføjer punkter - mindst 3."),
				t("Højreklik for at afslutte. Punktskydata indlæses, og beregningen kører automatisk; instruktionsboksen viser hvilket trin den er i."),
			])),
			S(t("Hvad der beregnes"), t("Værktøjet lægger et referenceplan, der passer bedst muligt til polygonens egen rand, og sammenligner det med punktskyens observerede overflade inde i polygonen. Stiger overfladen over planet, er det <b>påfyldning</b>; ligger den under, er det <b>udgravning</b>.")),
			S(t("Resultat"), t("Areal, påfyldning og udgravning vises samlet i ét resultatlabel. Påfyldning er positiv og udgravning negativ. Selve polygonet og den beregnede overflade bevares; polygonets punktlabels skjules efter beregningen.")),
			S(t("Hvis noget går galt"), t("Kan punktskyen ikke indlæses, findes der ingen punkter inden for polygonen, eller mislykkes beregningen, vises en fejlmeddelelse - og der oprettes <b>ingen</b> registrering. Tager beregningen længere tid end forventet, får du besked om, hvilket trin den arbejder på. <b>ESC</b> annullerer, mens den kører.")),
			S(t("Tændte og slukkede klasser"), t("Beregningen bruger kun de klasser, der er tændt, når den kører: når du afslutter polygonen, og igen når du flytter et punkt. Sluk fx ledningerne for at beregne volumenet uden dem; hullerne efter dem lukkes. At tænde eller slukke en klasse ændrer ikke i sig selv et færdigt resultat; det sker først ved næste beregning. Resultatet viser, hvilke klasser der er udeladt. Mangler der punkter i for stor en del af polygonen, beregnes der ikke, og du får en besked i stedet.")),
			S(t("Tips"), ul([
				t("Sæt punkterne tæt nok til, at polygonen følger randen - referenceplanet bygges netop på randen."),
				t("Vælg et område, hvor punktskyen er indlæst i god opløsning (zoom ind, så detaljerne er hentet), før du beregner."),
			])),
			S(t("Vigtigt"), t("Resultatet gælder for den geometri, det blev beregnet på. Flytter du et punkt på polygonet, genberegnes areal og volumen automatisk, <b>når du slipper punktet</b> (en kort besked vises); indtil da står resultatet som forældet. Mislykkes genberegningen, bliver det gamle resultat stående som forældet, og du får en besked - objektet slettes aldrig. Efter genindlæsning vises polygonen og de gemte tal uden ny beregning; overfladen (TIN) gemmes ikke og vises igen, når polygonen redigeres og beregnes igen.")),
		],
		related: ["polygon", "measure-area", "cross-section"],
	},

	// ─────────────────────────── SPECIALVÆRKTØJER ────────────────────────
	{
		id: "pipe",
		title: t("Rør"),
		category: "special",
		summary: t("Bestem et rørs udvendige dimension, centerlinje og koter ud fra den synlige punktsky-overflade."),
		keywords: [t("rør"), t("pipe"), t("ledning"), t("cylinder"), t("dimension"), t("diameter"), t("kote"), t("centerlinje")],
		sections: [
			S(t("Sådan gør du"), step([
				t("Aktivér <b>Rør</b>."),
				t("Klik to punkter langs røret for at angive retningen. Efter det første punkt følger en streg cursoren, og den stopper ved <b>0,5 m</b> - klikker du længere væk, sættes punktet i enden af stregen."),
				t("Følg værktøjets instruktioner øverst i vieweren for at pege på rørets overflade langs dets forløb."),
				t("Værktøjet tilpasser en cylinder til punkterne og udleder centerlinje, udvendig dimension og koter."),
			])),
			S(t("Resultat"), t("Ét Rør-objekt med en cylindervisning, en centerlinje og et resultatlabel med dimension og koter. Ved reduceret datadækning vises tilpasningen med en tydeligere farve som en påmindelse om lavere sikkerhed.")),
			S(t("Længden på retningsstykket"), t("Vælg et lige, tydeligt synligt stykke på ca. <b>0,3-0,5 m</b> (højst 0,5 m). Et meget langt stykke gør analyseområdet så stort, at beregningen ikke kan gennemføres - derfor kan stregen ikke blive længere end 0,5 m. Er røret længere, måler du blot et lokalt stykke af det.")),
			S(t("Vigtigt"), t("Rør registreres som et helt objekt - det har ikke enkelte redigerbare punkter. Vil du ændre det, digitalisér det igen. Se instruktionslinjen i vieweren for den præcise rækkefølge af input i den aktuelle version.")),
		],
		related: ["cross-section", "line", "pipe-trace"],
	},
	{
		id: "pipe-trace",
		title: t("Find rør"),
		category: "special",
		summary: t("Find og følg et synligt rør automatisk gennem punktskyen ud fra en brugerdefineret start - resultatet registreres direkte som centerlinje og toplinje."),
		keywords: [t("find rør"), t("rørsporing"), t("tracking"), t("automatisk"), t("følg rør"), t("ledning"), t("centerlinje"), t("diameter"), t("semi-automatisk")],
		sections: [
			S(t("Formål"), t("Hvor <i>Rør</i> beregner ét lokalt rørsnit ud fra dine punkter, følger <b>Find rør</b> rørets udstrækning videre gennem punktskyen så langt observationerne rækker.")),
			S(t("Sådan gør du"), step([
				t("Aktivér <b>Find rør</b>."),
				t("Udpeg rørets omtrentlige længderetning (to venstreklik)."),
				t("Udpeg flere punkter <b>på tværs af røret</b> på den synlige røroverflade - mindst 3, gerne 5-8, fordelt over hele den synlige bredde (fra den ene side over toppen til den anden). Højreklik afslutter."),
				t("Vent mens PointView følger røret. Undervejs vokser en <b>gul</b> linje fremad og en <b>magenta</b> linje bagud fra startpunktet (hvid prik); en prik for enden viser, hvor sporingen er nået til. <b>ESC</b> afbryder."),
			])),
			S(t("Resultat"), t("Når røret er fulgt, registreres resultatet <b>med det samme</b> - der er intet Accepter/Afvis-trin. Du får præcis to linjer under Registreringer: <b>Find rør N – Centerlinje</b> (rørets beregnede akse, en almindelig redigerbar linje) og <b>Find rør N – Toplinje</b> (rørets højeste punkt i tværsnittet langs forløbet, afledt af centerlinjen og rørets radius). Redigerer du centerlinjen, følger toplinjen med - også efter genindlæsning. Begge gemmes som almindelige registreringer.")),
			S(t("Hvis det ikke lykkes"), t("Kan røret ikke følges (fx ustabil start eller for få observationer), oprettes <b>intet</b>, og du får en kort besked. Slet et uønsket resultat som enhver anden registrering og kør værktøjet igen. Værktøjet afslutter sig selv efter hvert forsøg.")),
			S(t("Vigtigt"), ul([
				t("Vælg punkter spredt over den synlige røroverflade - røret behøver ikke være helt synligt."),
				t("Værktøjet regner kun i punktskyen for den <b>aktive model</b>, og resultatet knyttes til den - vælg modellen, før du starter."),
				t("Toplinjen kræver et rør, der ikke står lodret: sporingen stopper, før røret bliver stejlere end 75° fra vandret, og lodrette stigrør og skarpe knæk følges ikke."),
				t("Ligger klikkene langs røret i stedet for på tværs, eller bøjer den klikkede bue for lidt i forhold til punktskyens støj (smal synlig bue, meget støj), afvises starten hellere end at give en forkert diameter og centerlinje."),
				t("PointView stopper hellere sporingen end at fortsætte langt ind på en anden struktur, så en kort centerlinje kan være korrekt."),
			])),
			S(t("Tip"), t("Er resultatet dårligt, så prøv først en bedre initialisering (tydeligere retning, flere veludspredte overfladepunkter) frem for at ændre avancerede parametre.")),
		],
		related: ["pipe", "line", "edit-line"],
	},
	{
		id: "cross-section",
		title: t("Tværprofilrapport"),
		category: "special",
		summary: t("Digitalisér et forløb og generér automatisk en serie tværprofiler med oversigt og PDF-rapport."),
		keywords: [t("tværprofil"), t("profil"), t("tværsnit"), t("rapport"), t("pdf"), t("station"), t("forløb"), t("tracé")],
		sections: [
			S(t("Sådan gør du"), step([
				t("Aktivér <b>Tværprofilrapport</b> (under Rapporter i værktøjslinjen)."),
				t("Digitalisér forløbet (tracéet) med venstreklik for hvert punkt, højreklik afslutter."),
				t("Angiv profilafstand og profilbredde, når du bliver spurgt."),
				t("Profilerne genereres langs forløbet, og en rapport dannes."),
			])),
			S(t("Resultat"), t("Et Tværprofil-objekt: forløbet som en redigerbar linje, markører for hver profilstation, en top-view-oversigt og en PDF-rapport.")),
			S(t("Tip"), t("Redigér forløbet som en almindelig linje og åbn rapporten igen med <b>Vis rapport</b> fra objektmenuen - den regenereres ud fra den aktuelle geometri. Det samme gør dobbeltklik på Tværprofil-objektet (eller <b>Vis rapport</b> i menuen) i Registreringer. Rapportvinduet kan flyttes (træk i titellinjen) og lukkes med krydset.")),
		],
		related: ["line", "edit-line", "grave-report"],
	},
	{
		id: "grave-report",
		title: t("Graverapport"),
		category: "special",
		summary: t("Beregn udgravning og påfyldning automatisk for den indlæste punktsky - uden at digitalisere - og gem en rapport som PDF."),
		keywords: [t("graverapport"), t("gravning"), t("udgravning"), t("påfyldning"), t("volumen"), t("cut"), t("fill"), t("grube"), t("dynge"), t("rapport"), t("pdf"), t("automatisk")],
		sections: [
			S(t("Sådan gør du"), step([
				t("Aktivér <b>Graverapport</b> under Rapporter i værktøjslinjen."),
				t("Er der kun én indlæst punktsky, beregnes graverapporten med det samme. Er der flere indlæste, vælger du først den, rapporten skal gælde."),
				t("Fremdriften vises, og du kan annullere."),
				t("Udgravning (rød) og påfyldning (grøn) markeres automatisk på selve punkterne i 3D, med labels med volumen, areal og dybde, og lægges som et objekt under <b>Målinger</b> i Registreringer - de bliver, også når dialogen lukkes. Øjet viser/skjuler dem, papirkurven fjerner dem, og dobbeltklik (eller <b>Vis rapport</b> i menuen) åbner rapporten igen."),
				t("Hent <b>Rapport (PDF)</b> i dialogen."),
			])),
			S(t("Hvad beregnes"), t("Punktskyen inddeles i et net af 0,3 m celler (medianhøjde pr. celle). Det oprindelige terræn rekonstrueres ud fra de punkter, der er klassificeret som jord (ellers ud fra den scannede flades yderrand). Celler under terrænet er <b>udgravning</b>, celler over er <b>påfyldning</b>; for hver tæller det største sammenhængende område. Volumen er højdeforskellen gange celleareal.")),
			S(t("Nøjagtighed"), t("Beregningen bruger kun de grove niveauer af punktskyens oktræ, så den er hurtig og ikke påvirker det, der er indlæst til visning. Mod en beregning på fuld opløsning er afvigelsen målt til ca. 2 % ved mindst ca. 1000 punkter pr. m². Er skyen tyndere, vises en advarsel i dialogen og i rapporten.")),
			S(t("Resultater gemmes lokalt"), t("Et beregnet resultat huskes i din browser (pr. punktsky), så du kan hente rapporten igen uden at regne forfra. Intet skrives til delte data. På sigt kan beregningen køre som job efter punktskyens beregning og levere samme resultat.")),
		],
		related: ["cross-section"],
	},

	{
		id: "poi",
		title: t("POI (inspektion)"),
		category: "registrering",
		summary: t("Markér steder i punktskyen til inspektion: en nål med nummer og status, et skærmbillede, fotos, noter og et link, der lander præcis dér."),
		keywords: [t("poi"), t("inspektion"), t("inspektionspunkt"), t("nål"), t("pin"), t("status"), t("foto"), t("skærmbillede"), t("tur"), t("rapport"), t("link"), t("geonote"), t("frist"), t("ansvarlig")],
		sections: [
			S(t("Sådan gør du"), step([
				t("Vælg værktøjet <b>POI</b> under Registrering (eller <b>Ny POI</b> i POI-oversigten)."),
				t("Klik på det sted i punktskyen, du vil markere. POI'en får et nummer, og du får automatisk et skærmbillede og din nuværende visning med."),
				t("Kortet åbner af sig selv, når du har placeret POI'en, med markøren i titelfeltet. Skriv en titel og vælg status (<b>Åben</b>, <b>I gang</b>, <b>Løst</b>). Tilføj kategori, ansvarlig, frist, beskrivelse og fotos efter behov. Alt <b>gemmes automatisk</b>, mens du skriver - der er ingen Gem-knap, og kortet viser <i>Gemt automatisk</i>. Under fanen <b>Punktsky</b> foreslår PointView to billeder fra dannelsen af punktskyen, hvis kamera peger mod punktet. Vælg <b>Vælg selv</b> for at klikke de billeder til, der viser stedet bedst (nærmeste først), og klik igen for at fjerne et. Er forslagene taget fra den forkerte side, så vælg <b>Vend retning</b>."),
				t("<b>POI-oversigten</b> åbner du fra menuen <b>Rapporter</b>, ved at dobbeltklikke på gruppen <b>POI'er</b> i <b>Registreringer</b> eller med nål-ikonet i Registreringer. Klik på en POI i listen eller på nålen i 3D for at åbne dens kort og flyve derhen - eller dobbeltklik på POI'en i Registreringer (menuen <b>Åbn POI</b>). Kortet har fanerne <b>Detaljer</b>, <b>Billeder</b>, <b>Punktsky</b> og <b>Sted &amp; link</b>. <b>Inspektionsturen</b> (pilene øverst i oversigten) går gennem udvalget en POI ad gangen, og afspil-knappen gør det automatisk."),
			])),
			S(t("Nålen"), t("Ringens farve er status, tallet er POI-nummeret, og en lille rød prik betyder, at fristen er overskredet. Dobbeltklik på en nål flyver kameraet til POI'ens gemte visning.")),
			S(t("Filtre og rapport"), t("Chips over listen viser kun åbne, igangværende, løste eller forfaldne POI'er, og søgefeltet søger i titel, kategori, ansvarlig og beskrivelse. Menuen (tre prikker) laver en <b>Inspektionsrapport (PDF)</b> over det viste udvalg med skærmbilleder og fotos, og kan eksportere og importere POI'er som en fil.")),
			S(t("Links"), t("<b>Kopiér link</b> laver et QuickView-link: det kræver ikke login, åbner hurtigt og viser kun denne POI (punktskyen og POI'ens visning, ikke dine øvrige registreringer). Du vælger, hvor længe det gælder. <b>Integrationslinket</b> (kræver login) åbner PointView ved POI'en og kan bruges fra fx Geonote. Har du et Geonote-link, kan du lægge det på POI'en og åbne det derfra - PointView opretter ingen opgaver i Geonote af sig selv.")),
			S(t("Hvor data bor"), t("POI'er gemmes lokalt i din browser, pr. punktsky, og står også som almindelige registreringer (værktøjet POI) i Registreringer, hvor de kan omdøbes, flyttes og eksporteres. De kan eksporteres til en fil og importeres igen, indtil de på sigt gemmes i databasen.")),
		],
		related: ["point", "grave-report"],
	},

	// ─────────────────────────────── MÅLINGER ────────────────────────────
	{
		id: "measure-point",
		title: t("Mål punkt"),
		category: "maalinger",
		summary: t("Mål et punkts koordinater: X, Y og Z på tre linjer."),
		keywords: [t("mål"), t("punkt"), t("koordinat"), t("koordinater"), t("x"), t("y"), t("z"), t("kote"), t("position"), t("måling")],
		sections: [
			S(t("Sådan gør du"), step([
				t("Aktivér <b>Mål punkt</b> under <b>Måling</b> i værktøjslinjen."),
				t("Venstreklik på det punkt i punktskyen, du vil måle. Værktøjet er klar til næste punkt med det samme."),
			])),
			S(t("Resultat"), t("Det orange resultat viser punktets koordinater på tre linjer: <b>X</b>, <b>Y</b> og <b>Z</b> (3 decimaler) - i punktskyens koordinatsystem, som alle andre koordinater i PointView. Brug kopi-ikonet ved resultatet for at kopiere teksten.")),
			S(t("Forskel fra Punkt"), t("<b>Mål punkt</b> er en <i>måling</i> (ligger under Måling og er ikke med i Gem registreringer). Skal punktet registreres, så brug tegneværktøjet <i>Punkt</i>.")),
			S(t("Tip"), t("Flyt punktet, og koordinaterne opdateres med det samme. Med <i>Snap</i> slået til kan du måle et eksisterende, synligt punkts præcise koordinater.")),
		],
		related: ["point", "measure-distance", "snap"],
	},
	{
		id: "measure-distance",
		title: t("Mål afstand"),
		category: "maalinger",
		summary: t("Mål den samlede længde langs en række punkter."),
		keywords: [t("mål"), t("afstand"), t("længde"), t("distance"), t("måling")],
		sections: [
			S(t("Sådan gør du"), step([
				t("Aktivér <b>Mål afstand</b>."),
				t("Venstreklik for hvert punkt langs strækningen."),
				t("Højreklik for at afslutte."),
			])),
			S(t("Resultat"), t("Den samlede afstand langs alle punkterne vises ved målingen.")),
		],
		related: ["measure-height", "measure-angle", "line"],
	},
	{
		id: "measure-area",
		title: t("Mål areal"),
		category: "maalinger",
		summary: t("Mål areal og omkreds af et område."),
		keywords: [t("mål"), t("areal"), t("omkreds"), t("flade"), t("måling")],
		sections: [
			S(t("Sådan gør du"), step([
				t("Aktivér <b>Mål areal</b>."),
				t("Venstreklik for hvert hjørne."),
				t("Højreklik for at afslutte."),
			])),
			S(t("Resultat"), t("Areal og omkreds vises ved målingen.")),
			S(t("Tip"), t("Skal volumen med, brug <i>Mål areal og volumen</i> i stedet.")),
		],
		related: ["measure-distance", "area-volume", "polygon"],
	},
	{
		id: "measure-height",
		title: t("Mål højde"),
		category: "maalinger",
		summary: t("Mål højdeforskellen mellem to punkter, med vandret afstand og hældningsvinkel."),
		keywords: [t("mål"), t("højde"), t("kote"), t("højdeforskel"), t("hældning"), t("vinkel"), t("måling")],
		sections: [
			S(t("Sådan gør du"), step([
				t("Aktivér <b>Mål højde</b>."),
				t("Venstreklik på det første punkt, derefter det andet."),
			])),
			S(t("Resultat"), t("Højdeforskel, vandret afstand og hældningsvinkel vises ved målingen.")),
		],
		related: ["measure-distance", "measure-angle", "measure-azimuth"],
	},
	{
		id: "measure-angle",
		title: t("Mål vinkel"),
		category: "maalinger",
		summary: t("Mål vinklen mellem to ben i et midterpunkt (vinklens spids), med benenes længder."),
		keywords: [t("mål"), t("vinkel"), t("angle"), t("grader"), t("ben"), t("spids"), t("måling")],
		sections: [
			S(t("Sådan gør du"), step([
				t("Aktivér <b>Mål vinkel</b>."),
				t("Venstreklik på det første punkt (enden af det ene ben)."),
				t("Venstreklik på vinklens spids (midterpunktet)."),
				t("Venstreklik på det tredje punkt (enden af det andet ben)."),
			])),
			S(t("Resultat"), t("Vinklen i spidsen (0-180° i 3D, 1 decimal) vises som en bue, og hvert bens længde vises på benet. Det orange resultat viser vinklen og begge benlængder.")),
			S(t("Tip"), t("Flyt et af de tre punkter, og alt opdateres med det samme. Antallet af punkter er fast - en måling kan ikke have punkter indsat eller slettet; slet hele målingen i stedet.")),
		],
		related: ["measure-distance", "measure-height", "measure-azimuth"],
	},
	{
		id: "measure-circle",
		title: t("Mål cirkel"),
		category: "maalinger",
		summary: t("Mål en cirkel gennem tre punkter på dens kant: radius, diameter, omkreds, areal og centrum."),
		keywords: [t("mål"), t("cirkel"), t("circle"), t("radius"), t("diameter"), t("omkreds"), t("areal"), t("centrum"), t("måling")],
		sections: [
			S(t("Sådan gør du"), step([
				t("Aktivér <b>Mål cirkel</b>."),
				t("Venstreklik på tre forskellige punkter på cirklens kant."),
			])),
			S(t("Resultat"), t("Cirklen tegnes i planen gennem de tre punkter, med centrum markeret og en stiplet radius. Det orange resultat viser radius, diameter, omkreds, areal og centrum (X, Y, Z).")),
			S(t("Forskel fra Cirkel"), t("<b>Mål cirkel</b> er en <i>måling</i> (ligger under Målinger og er ikke med i Gem registreringer). Skal en cirkel registreres som en polygon, så brug tegneværktøjet <i>Cirkel</i>.")),
			S(t("Ligger punkterne for tæt på en linje?"), t("Så findes der ingen cirkel, og der vises intet resultat, før du flytter et punkt, så de er tydeligere fordelt.")),
		],
		related: ["circle", "measure-area", "measure-distance"],
	},
	{
		id: "measure-azimuth",
		title: t("Mål azimut"),
		category: "maalinger",
		summary: t("Mål retningen fra det første til det andet punkt som azimut: grader med uret fra nettets nord."),
		keywords: [t("mål"), t("azimut"), t("azimuth"), t("retning"), t("nord"), t("kompas"), t("grader"), t("måling")],
		sections: [
			S(t("Sådan gør du"), step([
				t("Aktivér <b>Mål azimut</b>."),
				t("Venstreklik på udgangspunktet."),
				t("Venstreklik på målpunktet."),
			])),
			S(t("Resultat"), t("Azimut vises i grader med uret fra nord (0° = nord, 90° = øst, 180° = syd, 270° = vest) med 2 decimaler, sammen med en nordlinje og en bue ved udgangspunktet. Det orange resultat viser også den vandrette afstand.")),
			S(t("Nettets nord"), t("Retningen regnes direkte i koordinatsystemet (nettets nord, fx UTM-nord) - ikke geografisk nord, og uafhængigt af punktskyens projektion. Forskellen er typisk under ét grad i Danmark. Højden indgår ikke i azimut.")),
		],
		related: ["measure-distance", "measure-height", "measure-angle"],
	},

	// ─────────────────────────────── REDIGERING ──────────────────────────
	{
		id: "snap",
		title: t("Snapping"),
		category: "redigering",
		summary: t("Snapping låser et punkt præcist til en anden eksisterende position. Der er tre situationer."),
		keywords: [t("snap"), t("snapping"), t("lås"), t("fikspunkt"), t("sammenfald"), t("koordinat"), t("xyz"), t("punktsky")],
		sections: [
			S(t("Snap under registrering"), t("Når du placerer et nyt punkt tæt på et eksisterende, <b>synligt</b> registreret punkt, snapper det oveni - i alle Registrerings- og Måle-værktøjer. <b>Alle</b> rigtige punkter i en registrering kan snappes til - også et punkt, der er indsat af Trim, Del linje eller Sæt punkt ved skæring. Skjulte registreringer (øjet slået fra i Registreringer) snappes der ikke til.")),
			S(t("Slå Snap til/fra og ændr afstanden"), ul([
				t("<b>Til/fra</b>: Indstillinger → <b>Egenskaber</b> → Interaktion → <b>Snap</b>. Det er den ene kontakt for al automatisk snap i PointView: Registrering, Måling og tegningerne i sidepanelet. Slået fra placeres punkter præcis, hvor du klikker i punktskyen. Standard: til."),
				t("<b>Snap-afstand</b>: samme sted. Afstanden måles i <b>skærm-pixels</b> (standard 15 px, gyldigt 3-50), ikke i meter - så snappet føles ens, uanset hvor langt du er zoomet ind eller ud. Tegningerne og Mål i sidepanelet arbejder i meter og bruger en fast snap-afstand; de følger kun til/fra-kontakten."),
				t("De eksplicitte handlinger <i>Snap til punktsky</i> og <i>Snap til andet punkt</i> er kommandoer, ikke automatisk snap - de virker altid, uanset kontakten."),
			])),
			S(t("Snap til punktsky"), t("Flytter det valgte punkt til den nærmeste position i selve punktskyen. Dette er en <b>eksplicit XYZ-handling</b> - punktet flyttes i alle tre akser.")),
			S(t("Snap til andet punkt"), t("Flytter det valgte punkt <b>præcist til X-, Y- og Z-koordinaten</b> for et andet registreret punkt, du udpeger. Brug denne, når to punkter reelt skal mødes i 3D.")),
			S(t("Vigtigt"), t("Snapping er ikke det samme som en plan-geometrisk skæring. <i>Trim</i>, <i>Forlæng</i> og <i>Sæt punkt ved skæring</i> beregnes i planen (set fra oven) og ændrer ikke automatisk højderne, så to linjer mødes i 3D. Snapping gør netop det - men kun fordi du beder om det.")),
		],
		related: ["snap-cloud", "snap-other-point", "trim-line", "intersection"],
	},
	{
		id: "edit-point",
		title: t("Redigér et punkt"),
		category: "redigering",
		summary: t("Handlinger for et enkelt punkt: flyt, snap og slet. Menuen hedder Linjepunkt, Endepunkt eller Punkt."),
		keywords: [t("punkt"), t("linjepunkt"), t("vertex"), t("flyt"), t("slet"), t("redigér"), t("position")],
		sections: [
			S(t("Sådan åbner du menuen"), t("Shift + højreklik direkte på punktet.")),
			S(t("Handlinger"), ul([
				t("<b>Flyt punkt</b> - forhåndsvis en ny position i punktskyen, venstreklik bekræfter, ESC annullerer."),
				t("<b>Flyt punkt i Z…</b> - flytter punktet lodret et angivet antal cm op eller ned. Se <i>Flyt punkt i Z</i>."),
				t("<b>Snap til punktsky</b> - flytter punktet til nærmeste punktsky-position (XYZ)."),
				t("<b>Snap til andet punkt…</b> - udpeg et andet registreret punkt; punktet flyttes præcist til dets X, Y og Z."),
				t("<b>Slet punkt</b> - fjerner punktet. Er geometrien allerede på sit minimum, slettes hele objektet, og menupunktet hedder da <b>Slet objekt</b>."),
			])),
			S(t("Vigtigt"), t("Handlingerne virker <b>kun</b> på det punkt, du valgte. Ligger et andet objekts endepunkt oveni, flyttes det ikke med. Bryder du en sådan sammenfald, får du en kort besked om det.")),
		],
		related: ["move-point-z", "snap", "delete-point", "edit-endpoint", "selection"],
	},
	{
		id: "edit-endpoint",
		title: t("Redigér et endepunkt"),
		category: "redigering",
		summary: t("En åben linjes første eller sidste punkt har alle punkt-handlinger plus forlæng, trim og forbind."),
		keywords: [t("endepunkt"), t("endpoint"), t("første punkt"), t("sidste punkt"), t("forlæng"), t("trim"), t("forbind")],
		sections: [
			S(t("Ekstra handlinger"), ul([
				t("<b>Forlæng til reference…</b> - forlænger linjen, til den rammer en anden linje. Se <i>Forlæng til reference</i>."),
				t("<b>Forlæng endesegment…</b> - flytter endepunktet et angivet antal cm frem eller tilbage langs endesegmentets egen retning, uden en referencelinje. Se <i>Forlæng endesegment</i>."),
				t("<b>Trim til reference…</b> - afkorter linjen ved skæringen med en anden linje. Se <i>Trim til reference</i>."),
				t("<b>Forbind til andet endepunkt…</b> - flytter endepunktet præcist oven i et andet endepunkt (XYZ). Se <i>Forbind til andet endepunkt</i>."),
			])),
			S(t("Tip"), t("De almindelige punkt-handlinger (Flyt, Flyt i Z, Snap) er også med - se <i>Redigér et punkt</i>.")),
		],
		related: ["extend-reference", "extend-segment", "trim-reference", "connect-endpoint", "edit-point"],
	},
	{
		id: "edit-segment",
		title: t("Redigér et segment"),
		category: "redigering",
		summary: t("Handlinger for linjestykket mellem to punkter: indsæt, del, trim mod linje, konstruér og udvid valget."),
		keywords: [t("segment"), t("linjestykke"), t("kant"), t("indsæt punkt"), t("del linje"), t("trim"), t("konstruktion")],
		sections: [
			S(t("Sådan åbner du menuen"), t("Shift + højreklik på linjen mellem to punkter, uden at ramme et punkt.")),
			S(t("Redigering"), ul([
				t("<b>Indsæt punkt</b> - nyt punkt på segmentet, der hvor du klikkede."),
				t("<b>Del linje her</b> - deler linjen i to selvstændige linjer."),
				t("<b>Trim mod linje…</b> - afkorter linjen ved skæringen med en anden linje. Se <i>Trim mod linje</i>."),
			])),
			S(t("Konstruktion"), ul([
				t("<b>Fast vinkel…</b> - sætter vinklen ved et fælles punkt til en valgt værdi."),
				t("<b>Parallel med reference…</b> / <b>Vinkelret på reference…</b> - drejer segmentet efter en anden linje."),
				t("<b>Sæt punkt ved skæring…</b> - indsætter et punkt, hvor linjen krydser en anden linje."),
			])),
			S(t("Udvid valg"), ul([
				t("<b>Vælg sammenhængende del…</b> - se <i>Redigér en del af en linje</i>."),
				t("<b>Vælg hele objektet</b> - åbner menuen for hele linjen."),
			])),
		],
		related: ["insert-point", "split-line", "trim-line", "intersection", "fixed-angle", "parallel-perpendicular", "edit-line-part"],
	},
	{
		id: "edit-line",
		title: t("Redigér en hel linje"),
		category: "redigering",
		summary: t("Handlinger, der påvirker hele linjen: udretning, forenkling, retning og sammenføjning."),
		keywords: [t("hel linje"), t("objekt"), t("ret ud"), t("forenkle"), t("ortogonaliser"), t("vend retning"), t("saml"), t("fordel")],
		sections: [
			S(t("Sådan åbner du menuen"), t("Vælg <b>Vælg hele objektet</b> fra segment-menuen, eller Shift + højreklik på geometrien som helhed.")),
			S(t("Geometri"), ul([
				t("<b>Ret ud i 2D</b> - retter de mellemliggende punkter ud på linjen mellem de to endepunkter, i planen; endepunkterne selv rører sig ikke, og højderne er uændrede."),
				t("<b>Ret ud i 3D</b> - en udjævning af <b>alle</b> punkter i hele linjen (eller det valgte stykke), inkl. begge endepunkter. Ingen punkter er fastholdt."),
				t("<b>Forenkle</b> - fjerner overflødige punkter inden for den tilladte pilhøjde."),
				t("<b>Ortogonaliser</b> - retter hjørner tæt på 90°/180° helt ud."),
				t("<b>Fordel punkter lige</b> - jævner afstanden mellem de mellemliggende punkter uden at ændre linjens forløb."),
			])),
			S(t("Positionering"), ul([
				t("<b>Flyt linje i Z…</b> - flytter alle linjens punkter lodret det samme antal cm."),
				t("<b>Snap alle punkter til punktsky</b> - snapper hvert punkt til sin egen nærmeste punktsky-position."),
			])),
			S(t("Struktur"), ul([
				t("<b>Vend retning</b> - vender punktrækkefølgen; geometrien i rummet er uændret."),
				t("<b>Saml med anden linje…</b> - føjer en anden linje til denne. Se <i>Split og saml</i>."),
				t("<b>Trim mod linje…</b> - afkorter linjen ved skæringen med en anden linje."),
			])),
			S("", ul([t("<b>Slet objekt</b> - fjerner hele linjen.")])),
		],
		related: ["straighten-simplify", "distribute-points", "reverse-direction", "merge-lines", "trim-line"],
	},
	{
		id: "edit-line-part",
		title: t("Redigér en del af en linje"),
		category: "redigering",
		summary: t("Vælg et sammenhængende stykke af en linje og redigér kun det."),
		keywords: [t("del af linje"), t("sammenhængende del"), t("interval"), t("stykke"), t("ret ud"), t("forenkle"), t("omtegn")],
		sections: [
			S(t("Sådan gør du"), step([
				t("Fra segment- eller objektmenuen: <b>Vælg sammenhængende del…</b>."),
				t("Venstreklik på stykkets startpunkt, derefter dets slutpunkt. ESC annullerer."),
				t("Menuen for det valgte stykke åbnes."),
			])),
			S(t("Handlinger"), ul([
				t("<b>Ret ud i 2D / 3D</b> og <b>Forenkle</b> - altid tilgængelige."),
				t("<b>Ortogonaliser</b> og <b>Fordel punkter lige</b> - når stykket har mindst ét indre punkt."),
				t("<b>Fast vinkel…</b> - når stykket er præcis to segmenter omkring ét punkt."),
				t("<b>Omtegn del…</b> - tegn et nyt forløb for stykket. Se <i>Omtegn en del</i>."),
			])),
		],
		related: ["straighten-simplify", "reshape-part", "fixed-angle", "edit-segment"],
	},
	{
		id: "edit-polygon",
		title: t("Redigér et polygon"),
		category: "redigering",
		summary: t("Et polygon kan flyttes og forenkles pr. punkt, men de linjespecifikke handlinger er bevidst udeladt."),
		keywords: [t("polygon"), t("flade"), t("lukket"), t("redigér"), t("forenkle"), t("ortogonaliser")],
		sections: [
			S(t("Pr. punkt"), t("Shift + højreklik på et hjørne: <b>Flyt punkt</b>, <b>Snap til punktsky</b>, <b>Snap til andet punkt</b>, <b>Slet punkt</b> - som for en linje.")),
			S(t("Hele polygonet"), t("Shift + højreklik på fladen: <b>Forenkle</b>, <b>Ortogonaliser</b>, <b>Snap alle punkter til punktsky</b>, <b>Slet objekt</b>.")),
			S(t("Vigtigt"), t("Ret ud, Vend retning, Trim, Saml og Vælg sammenhængende del gælder ikke for en lukket figur og vises ikke. Efter Forenkle/Ortogonaliser tjekkes det, at figuren stadig er gyldig (ingen selvskæring).")),
		],
		related: ["polygon", "straighten-simplify", "edit-point"],
	},

	// ── operations (individually openable) ──
	{
		id: "move-point",
		title: t("Flyt punkt"),
		category: "redigering",
		summary: t("Flyt det valgte punkt til en ny position i punktskyen."),
		keywords: [t("flyt"), t("punkt"), t("position"), t("træk")],
		sections: [
			S(t("Sådan gør du"), step([
				t("Shift + højreklik på punktet → <b>Flyt punkt</b>."),
				t("Bevæg musen i punktskyen - den nye position og de tilstødende segmenter forhåndsvises."),
				t("Venstreklik bekræfter. ESC annullerer uden ændring."),
			])),
			S(t("Vigtigt"), t("Kun det valgte punkt flyttes. Du kan også trække punktets kugle direkte med musen.")),
		],
		related: ["move-point-z", "snap-cloud", "snap-other-point", "edit-point"],
	},
	{
		id: "move-point-z",
		title: t("Flyt punkt i Z"),
		category: "redigering",
		summary: t("Flyt det valgte punkt lodret med et angivet antal cm - op eller ned."),
		keywords: [t("flyt"), t("z"), t("højde"), t("lodret"), t("op"), t("ned"), t("cm")],
		sections: [
			S(t("Sådan gør du"), step([
				t("Shift + højreklik på punktet (enkeltstående, mellem- eller endepunkt) → <b>Flyt punkt i Z…</b>."),
				t("Skriv et antal cm i feltet, eller vælg en af genvejsknapperne. Positivt tal flytter op, negativt ned."),
				t("Klik <b>Fortsæt</b> for at gennemføre. ESC eller Annuller afbryder uden ændring."),
			])),
			S(t("Vigtigt"), t("Kun X og Y er uændrede - der ændres udelukkende i højden. Gælder alle punkttyper: enkeltstående punkt-objekter, almindelige linjepunkter og endepunkter.")),
		],
		related: ["move-point", "set-absolute-z", "edit-point", "edit-endpoint"],
	},
	{
		id: "move-line-z",
		title: t("Flyt linje i Z"),
		category: "redigering",
		summary: t("Flyt hele linjen lodret med en valgt afstand eller rørdimension - op eller ned. Også for et segment eller en sammenhængende del."),
		keywords: [t("flyt linje i z"), t("flyt segment i z"), t("z"), t("højde"), t("lodret"), t("op"), t("ned"), t("cm"), t("dimension"), t("godstykkelse"), t("vand"), t("spildevand"), t("fjernvarme")],
		sections: [
			S(t("Sådan gør du"), step([
				t("Shift + højreklik på linjen (ikke på et punkt eller segment) → <b>Flyt linje i Z…</b>. Højreklik på et segment → <b>Flyt segment i Z…</b>; på en valgt sammenhængende del → <b>Flyt del i Z…</b>."),
				t("Vælg fanen <b>Standard</b>, <b>Vand</b>, <b>Spildevand</b> eller <b>Fjernvarme</b>, og klik på en afstand eller dimension. Du kan også skrive et antal cm direkte i <b>Z-forskydning</b>."),
				t("Vælg retning med <b>−</b> (ned) eller <b>+</b> (op). Dialogen åbner med retningen ned."),
				t("Klik <b>Flyt</b> for at gennemføre. <b>Nulstil</b> rydder valgene. ESC eller Annuller afbryder uden ændring."),
			])),
			S(t("Rørdimensioner"), t("En dimension flytter linjen hele dimensionen: 110 mm giver 11,0 cm. På <b>Spildevand</b> regnes godstykkelsen med sit eget fortegn: med retning ned giver 110 mm og 5 mm gods −10,5 cm med <b>+</b> og −11,5 cm med <b>−</b>. På <b>Fjernvarme</b> flyttes linjen fra toppen af kapperøret til rørets centerlinje, altså halvdelen af kappens diameter (præisolerede stålrør, enkeltrør, serie 1): DN 100 har kappe Ø200 mm og giver 10,0 cm.")),
			S(t("Vigtigt"), t("Alle de involverede punkter flyttes lige meget i højden, så formen er uændret; X og Y er uændrede. For en linje er det alle punkter, for et segment dets to punkter, for en sammenhængende del delens punkter (begge ender med).")),
		],
		related: ["move-point-z", "set-absolute-z", "snap-cloud"],
	},
	{
		id: "set-absolute-z",
		title: t("Sæt kote"),
		category: "redigering",
		summary: t("Sæt det valgte punkts absolutte kote (Z) direkte, i stedet for at flytte det et antal cm."),
		keywords: [t("sæt kote"), t("kote"), t("absolut z"), t("højde"), t("niveau")],
		sections: [
			S(t("Sådan gør du"), step([
				t("Shift + højreklik på punktet (enkeltstående, mellem- eller endepunkt) → <b>Sæt kote…</b>."),
				t("Skriv den ønskede kote i meter."),
				t("Klik <b>Fortsæt</b> for at gennemføre. ESC eller Annuller afbryder uden ændring."),
			])),
			S(t("Vigtigt"), t("Forskellen fra <i>Flyt punkt i Z</i>: her indtaster du målkoten direkte (z' = den indtastede værdi), ikke en relativ ændring. X og Y er uændrede.")),
		],
		related: ["move-point-z", "edit-point", "edit-endpoint"],
	},
	{
		id: "snap-cloud",
		title: t("Snap til punktsky"),
		category: "redigering",
		summary: t("Flyt det valgte punkt til den nærmeste position i punktskyen - i alle tre akser."),
		keywords: [t("snap"), t("punktsky"), t("nærmeste"), t("xyz"), t("flyt")],
		sections: [
			S(t("Sådan gør du"), t("Shift + højreklik på punktet → <b>Snap til punktsky</b>. Punktet flytter straks til det nærmeste punktsky-punkt i 3D.")),
			S(t("Hele linjen"), t("Fra linjemenuen: <b>Snap alle punkter til punktsky</b> gør det samme for hvert punkt.")),
		],
		related: ["snap", "snap-other-point", "move-point"],
	},
	{
		id: "snap-other-point",
		title: t("Snap til andet punkt"),
		category: "redigering",
		summary: t("Flyt det valgte punkt præcist til X, Y og Z for et andet registreret punkt."),
		keywords: [t("snap"), t("andet punkt"), t("sammenfald"), t("xyz"), t("forbind"), t("mød")],
		sections: [
			S(t("Sådan gør du"), step([
				t("Shift + højreklik på punktet → <b>Snap til andet punkt…</b>."),
				t("Udpeg et andet registreret punkt (en stiplet linje viser målet)."),
				t("Venstreklik. Punktet flyttes præcist til målets X, Y og Z."),
			])),
			S(t("Vigtigt"), t("Dette er den rigtige måde at få to punkter til at mødes eksakt i 3D. Det er forskelligt fra Trim/Skæring, der beregnes i planen og ikke rører højderne. Klik ved siden af et gyldigt punkt gør ingenting - handlingen fortsætter, til du rammer et punkt eller trykker ESC.")),
		],
		related: ["snap", "connect-endpoint", "trim-line"],
	},
	{
		id: "delete-point",
		title: t("Slet punkt"),
		category: "redigering",
		summary: t("Fjern det valgte punkt fra geometrien."),
		keywords: [t("slet"), t("fjern"), t("punkt"), t("dobbeltklik")],
		sections: [
			S(t("Sådan gør du"), t("Shift + højreklik på punktet → <b>Slet punkt</b>. Eller dobbeltklik på punktets kugle.")),
			S(t("Vigtigt"), t("Er geometrien allerede på sit minimum (2 punkter for en linje, 3 for et polygon, 1 for et punkt-objekt), fjernes hele objektet, og menupunktet hedder <b>Slet objekt</b>.")),
		],
		related: ["edit-point", "split-line"],
	},
	{
		id: "insert-point",
		title: t("Indsæt punkt på et segment"),
		category: "redigering",
		summary: t("Sæt et nyt punkt ind på et linjestykke."),
		keywords: [t("indsæt"), t("punkt"), t("segment"), t("tilføj"), t("vertex")],
		sections: [
			S(t("Sådan gør du"), t("Shift + højreklik på segmentet → <b>Indsæt punkt</b>. Punktet indsættes dér, hvor du klikkede, med korrekt højde langs segmentet.")),
			S(t("Tip"), t("Hold musen tæt på et segments midte for at få vist et lille <b>+</b>-håndtag, der også indsætter et punkt.")),
		],
		related: ["edit-segment", "split-line"],
	},
	{
		id: "split-line",
		title: t("Del linje"),
		category: "redigering",
		summary: t("Del én linje i to selvstændige linjer."),
		keywords: [t("del"), t("split"), t("opdel"), t("klip"), t("to linjer")],
		sections: [
			S("", DIAGRAMS.split),
			S(t("Sådan gør du"), t("Shift + højreklik på et segment eller et indre punkt → <b>Del linje her</b>.")),
			S(t("Resultat"), t("To linjer, der mødes i delingspunktet med præcist samme koordinat. Den oprindelige linje bevarer sin identitet; den anden halvdel bliver en ny linje.")),
		],
		related: ["merge-lines", "insert-point", "edit-segment"],
	},
	{
		id: "merge-lines",
		title: t("Saml med anden linje"),
		category: "redigering",
		summary: t("Føj en anden linje til den valgte, så de bliver én."),
		keywords: [t("saml"), t("merge"), t("sammenføj"), t("forbind linjer"), t("join")],
		sections: [
			S(t("Sådan gør du"), step([
				t("Shift + højreklik på linjen → <b>Saml med anden linje…</b>."),
				t("Klik på den linje, der skal føjes til."),
			])),
			S(t("Resultat"), t("Én linje. Den valgte linje beholder sin identitet; den anden fjernes. Retningen rettes til, så den samlede linje læses fra ende til ende.")),
			S(t("Vigtigt"), t("Linjerne skal mødes i et fælles endepunkt inden for tolerancen. Er der et reelt hul, luk det først med <b>Forbind til andet endepunkt</b>.")),
		],
		related: ["split-line", "connect-endpoint", "edit-line"],
	},
	{
		id: "trim-line",
		title: t("Trim mod linje"),
		category: "redigering",
		summary: t("Afkort en linje præcist ved skæringen med en anden linje - beregnet set fra oven."),
		keywords: [t("trim"), t("afkort"), t("skæring"), t("mod linje"), t("klip"), t("reference")],
		sections: [
			S("", DIAGRAMS.trimLine),
			S(t("Sådan gør du"), step([
				t("Vælg et segment (den side, der skal beholdes) eller hele linjen. Shift + højreklik → <b>Trim mod linje…</b>."),
				t("Klik på den linje, der skal trimmes imod."),
			])),
			S(t("Resultat"), t("Linjen afkortes ved skæringen. Der indsættes et punkt i skæringspunktet - både på den trimmede linje og på den linje, du trimmede imod - så et senere punkt kan snappe til det.")),
			S(t("Vigtigt"), t("Skæringen beregnes i planen. Ligger de to linjer i forskellig højde, beholder hver linje sit eget højdeforløb. Trimning gør dem <b>ikke</b> automatisk sammenfaldende i 3D - vil du det, brug <i>Snap til andet punkt</i> bagefter.")),
			S(t("Tip"), t("Ved flere skæringer: vælger du hele linjen, fjernes den korteste overskydende ende; vælger du et segment, beholdes den side, segmentet ligger på.")),
		],
		related: ["trim-reference", "extend-reference", "intersection", "snap"],
	},
	{
		id: "trim-reference",
		title: t("Trim til reference"),
		category: "redigering",
		summary: t("Afkort en linje fra et valgt endepunkt og ind til skæringen med en anden linje."),
		keywords: [t("trim"), t("til reference"), t("endepunkt"), t("afkort"), t("skæring")],
		sections: [
			S(t("Sådan gør du"), step([
				t("Shift + højreklik på linjens endepunkt → <b>Trim til reference…</b>."),
				t("Klik på den linje, der skal trimmes imod."),
			])),
			S(t("Resultat"), t("Linjen afkortes fra det valgte endepunkt ind til den nærmeste skæring. Referencelinjen ændres ikke.")),
			S(t("Vigtigt"), t("Samme plan-geometriske model som <i>Trim mod linje</i> - forskellen er kun, at du starter fra et endepunkt. Højderne ændres ikke automatisk for at få linjerne til at mødes i 3D. Det nye endepunkt får sin højde fra den linje, der trimmes.")),
		],
		related: ["trim-line", "extend-reference", "edit-endpoint"],
	},
	{
		id: "extend-reference",
		title: t("Forlæng til reference"),
		category: "redigering",
		summary: t("Forlæng en linjes endepunkt, til det rammer en anden linje - i planen."),
		keywords: [t("forlæng"), t("extend"), t("til reference"), t("endepunkt"), t("forlængelse"), t("skæring")],
		sections: [
			S("", DIAGRAMS.extend),
			S(t("Sådan gør du"), step([
				t("Shift + højreklik på linjens endepunkt → <b>Forlæng til reference…</b>."),
				t("Klik på den linje, der skal forlænges hen til."),
			])),
			S(t("Resultat"), t("Endepunktet flyttes ud langs linjens egen retning til skæringen med referencelinjen. Der tilføjes ikke et nyt punkt.")),
			S(t("Vigtigt"), t("Skæringspunktet findes i planen. Det nye endepunkts højde beregnes ved at fortsætte linjens eget sidste segments hældning - <b>ikke</b> ved at overtage referencelinjens højde.")),
		],
		related: ["extend-segment", "trim-reference", "trim-line", "edit-endpoint"],
	},
	{
		id: "extend-segment",
		title: t("Forlæng endesegment"),
		category: "redigering",
		summary: t("Flyt et endepunkt et angivet antal cm frem eller tilbage langs endesegmentets egen retning - uden en referencelinje."),
		keywords: [t("forlæng"), t("endesegment"), t("afkort"), t("cm"), t("endepunkt"), t("retning")],
		sections: [
			S(t("Sådan gør du"), step([
				t("Shift + højreklik på linjens endepunkt → <b>Forlæng endesegment…</b>."),
				t("Skriv et antal cm i feltet, eller vælg en af genvejsknapperne. Positivt tal forlænger linjen (frem); negativt afkorter den (tilbage)."),
				t("Klik <b>Fortsæt</b> for at gennemføre. ESC eller Annuller afbryder uden ændring."),
			])),
			S(t("Vigtigt"), t("Retningen er altid endesegmentets egen nuværende 3D-retning - løber det skråt, fortsætter forlængelsen skråt. I modsætning til <i>Forlæng til reference</i> involverer dette ingen anden linje. En afkortning, der ville flytte endepunktet til eller forbi dets eget naboendepunkt, gennemføres ikke.")),
		],
		related: ["extend-reference", "trim-reference", "edit-endpoint"],
	},
	{
		id: "connect-endpoint",
		title: t("Forbind til andet endepunkt"),
		category: "redigering",
		summary: t("Flyt et endepunkt præcist oven i et andet endepunkt - eksakt sammenfald i 3D."),
		keywords: [t("forbind"), t("endepunkt"), t("luk gap"), t("saml"), t("sammenfald"), t("xyz")],
		sections: [
			S(t("Sådan gør du"), step([
				t("Shift + højreklik på linjens endepunkt → <b>Forbind til andet endepunkt…</b>."),
				t("Udpeg et andet endepunkt."),
			])),
			S(t("Resultat"), t("Det valgte endepunkt flyttes til det andet endepunkts præcise X, Y og Z. Det andet objekt ændres ikke.")),
			S(t("Vigtigt"), t("Dette er - sammen med <i>Snap til andet punkt</i> - den handling, hvor du bevidst beder om et 3D-sammenfald. Plan-geometriske handlinger som Trim gør ikke dette.")),
		],
		related: ["snap-other-point", "merge-lines", "edit-endpoint"],
	},
	{
		id: "intersection",
		title: t("Sæt punkt ved skæring"),
		category: "redigering",
		summary: t("Indsæt et punkt på den valgte linje, hvor den krydser en anden linje."),
		keywords: [t("skæring"), t("kryds"), t("sæt punkt"), t("intersection"), t("punkt ved skæring")],
		sections: [
			S("", DIAGRAMS.intersection),
			S(t("Sådan gør du"), step([
				t("Shift + højreklik på et segment → <b>Sæt punkt ved skæring…</b>."),
				t("Klik på den linje, der krydser."),
			])),
			S(t("Resultat"), t("Ét nyt punkt på den valgte linje i skæringspunktet (set fra oven). Den anden linje ændres ikke.")),
			S(t("Vigtigt"), t("Punktets højde interpoleres langs den linje, det indsættes på. Krydser du flere gange, vælges den skæring, der er nærmest, hvor du klikkede.")),
		],
		related: ["trim-line", "insert-point", "snap"],
	},
	{
		id: "fixed-angle",
		title: t("Fast vinkel"),
		category: "redigering",
		summary: t("Sæt vinklen ved et punkt til en bestemt værdi med mindst mulig ændring af geometrien."),
		keywords: [t("fast vinkel"), t("vinkel"), t("90 grader"), t("ret hjørne"), t("fixed angle")],
		sections: [
			S(t("Sådan gør du"), step([
				t("Shift + højreklik på et segment (eller vælg en sammenhængende del på præcis to segmenter) → <b>Fast vinkel…</b>."),
				t("Indtast den ønskede vinkel."),
			])),
			S(t("Resultat"), t("Det fælles punkt flyttes, så vinklen bliver præcis den valgte, med den mindst mulige forskydning. Nabopunkterne holdes fast.")),
			S(t("Vigtigt"), t("Kan vinklen ikke opnås inden for den tilladte pilhøjde, gennemføres handlingen ikke, og du får den beregnede afvigelse at vide.")),
		],
		related: ["parallel-perpendicular", "straighten-simplify", "edit-segment"],
	},
	{
		id: "parallel-perpendicular",
		title: t("Parallel og vinkelret på reference"),
		category: "redigering",
		summary: t("Drej et segment, så det bliver parallelt med eller vinkelret på et andet segment."),
		keywords: [t("parallel"), t("vinkelret"), t("reference"), t("drej"), t("juster segment"), t("retning")],
		sections: [
			S(t("Sådan gør du"), step([
				t("Shift + højreklik på segmentet → <b>Parallel med reference…</b> eller <b>Vinkelret på reference…</b>."),
				t("Klik på et andet segment (referencen)."),
			])),
			S(t("Resultat"), t("Segmentet drejes om sit eget midtpunkt til den nye retning; længden bevares, og der åbnes ikke et hul til nabosegmenterne. Beregnes i planen; højderne bevares.")),
			S(t("Vigtigt"), t("Overskrides den tilladte pilhøjde, gennemføres handlingen ikke.")),
		],
		related: ["fixed-angle", "perpendicular-line", "parallel-offset", "edit-segment"],
	},
	{
		id: "arc-from-sagitta",
		title: t("Bue fra pilhøjde"),
		category: "redigering",
		summary: t("Erstat et lige stykke af en linje med en cirkelbue, defineret ud fra pilhøjden mellem korden og buens top."),
		keywords: [t("bue"), t("pilhøjde"), t("arc"), t("cirkelbue"), t("sagitta"), t("rediger bue"), t("vend bue"), t("opløs bue"), t("kurve")],
		sections: [
			S(t("Hvad er pilhøjden?"), t("De to punkter, du har valgt (en sammenhængende del eller hele linjen), bliver buens <b>kord</b> - de rører aldrig. Pilhøjden er den vinkelrette afstand fra korden til buens højeste punkt, midt på buen. Jo større tal, jo mere buer linjen.")),
			S(t("Positiv og negativ side"), t("Fortegnet på pilhøjden bestemmer, hvilken side buen ligger på. Du kan også klikke <b>Vend bue</b> for at skifte side med samme talværdi, i stedet for at skrive et minus.")),
			S(t("Sådan gør du"), step([
				t("Vælg en sammenhængende del (mindst ét mellempunkt) eller hele linjen, → <b>Bue fra pilhøjde…</b>."),
				t("Skriv en pilhøjde i cm. Buen vises med det samme og opdateres, mens du skriver."),
				t("Klik <b>Vend bue</b> for at skifte side, eller <b>Godkend</b> for at gemme. ESC eller Annuller gendanner det oprindelige forløb præcist."),
			])),
			S(t("Antal punkter tilpasses automatisk"), t("Du behøver ikke tænke på, hvor mange punkter buen har - det klarer PointView selv, ud fra buens størrelse og den tilladte pilhøjde-tolerance (samme indstilling som Forenkle/Ret ud bruger). En stor eller kraftig bue får flere punkter; en lille bue nøjes med få. Punktantallet justeres live, mens du ændrer pilhøjden.")),
			S(t("Buen kan redigeres senere"), t("Shift + højreklik på et segment, der er en del af en bue, viser tre ekstra valg: <b>Rediger bue…</b> (åbner samme dialog igen med den nuværende pilhøjde), <b>Vend bue</b> (skifter side med ét klik) og <b>Opløs bue</b> (fjerner kun buens definition - de nuværende punkter og deres koordinater rører sig ikke, linjen bliver bare almindelige, frit redigerbare punkter igen).")),
			S(t("Vigtigt"), t("Flytter, sletter eller indsætter du selv et punkt inde i en bue (uden at bruge Rediger/Vend/Opløs), opløses buen automatisk - stille og roligt, uden fejlmeddelelse. Det er med vilje: PointView tvinger aldrig et manuelt flyttet punkt tilbage på buen.")),
		],
		related: ["interpolate-z", "set-slope", "distribute-points", "edit-line-part"],
	},
	{
		id: "interpolate-z",
		title: t("Interpolér Z mellem punkter"),
		category: "redigering",
		summary: t("Beregn en jævn, lineær højdeovergang mellem de to endepunkter i en valgt del af linjen."),
		keywords: [t("interpolér"), t("interpoler z"), t("højde"), t("kote"), t("jævn overgang"), t("lineær")],
		sections: [
			S(t("Sådan gør du"), t("Vælg en sammenhængende del (mindst ét mellempunkt) → <b>Interpolér Z mellem punkter</b>. Handlingen udføres med det samme.")),
			S(t("Resultat"), t("De to endepunkter i det valgte stykke beholder deres kote uændret. Hvert mellempunkts kote beregnes lineært mellem dem, efter hvor langt punktet ligger langs stykkets vandrette (plane) forløb - ikke efter punktnummer. X og Y ændres aldrig.")),
		],
		related: ["set-slope", "arc-from-sagitta", "set-absolute-z"],
	},
	{
		id: "set-slope",
		title: t("Sæt fald/hældning"),
		category: "redigering",
		summary: t("Beregn koter langs en del af linjen ud fra ét referencepunkt og en angivet hældning."),
		keywords: [t("fald"), t("hældning"), t("slope"), t("promille"), t("procent"), t("kote"), t("gradient")],
		sections: [
			S(t("Sådan gør du"), step([
				t("Vælg en sammenhængende del (mindst to punkter) → <b>Sæt fald/hældning…</b>."),
				t("Skriv hældningen og vælg enhed (‰ eller %). Vælg om Startpunkt eller Slutpunkt er referencen (den kote, der holdes fast)."),
				t("Klik <b>Vend retning</b> for at skifte fortegn, eller <b>Fortsæt</b> for at gennemføre."),
			])),
			S(t("Resultat"), t("Referencepunktets kote rører sig ikke. Alle andre punkter i stykket - inklusive den anden ende - får en ny kote beregnet fra referencekoten, hældningen og punktets vandrette afstand langs linjen. X og Y ændres aldrig.")),
			S(t("Vigtigt"), t("Hældningens fortegn er knyttet til linjens egen retning (fra start mod slut), ikke til hvilken ende du vælger som reference - at skifte reference-ende vender derfor ikke automatisk fald til stigning. Skal retningen vendes, bruges <b>Vend retning</b>.")),
		],
		related: ["interpolate-z", "arc-from-sagitta", "set-absolute-z"],
	},
	{
		id: "straighten-simplify",
		title: t("Ret ud, forenkle og ortogonaliser"),
		category: "redigering",
		summary: t("Tre måder at rydde op i en linjes eller et stykkes form på."),
		keywords: [t("ret ud"), t("straighten"), t("forenkle"), t("simplify"), t("ortogonaliser"), t("orthogonalize"), t("udjævn"), t("oprydning"), t("pilhøjde")],
		sections: [
			S("Ret ud i 2D", t("Flytter de mellemliggende punkter ud på den rette linje mellem de to endepunkter, i planen. Endepunkterne selv flyttes aldrig, og højderne holdes uændret.")),
			S("Ret ud i 3D", t("En udjævning af <b>alle</b> punkter - også begge endepunkter. Målet er den rette linje, der bedst passer til hele det valgte stykkes punkter (ikke kun de to ender), og hvert punkt flyttes ud på den, i alle tre akser.")),
			S(t("Forenkle"), t("Fjerner punkter, der ikke bidrager til formen, så længe linjen ikke afviger mere end den tilladte pilhøjde. Afvigelsen måles i ægte 3D - et punkt, der repræsenterer et vigtigt Z-knæk (fx et lodret knæk på en ledning), fjernes ikke, bare fordi linjen er næsten lige set fra oven.")),
			S(t("Ortogonaliser"), t("Retter hjørner, der er tæt på 90° eller 180°, helt ud - inden for vinkeltolerancen og pilhøjden.")),
			S(t("Vigtigt"), t("Grænserne sættes under Digitalisering → Indstillinger → Vektorredigering (<b>Maksimal pilhøjde</b>, <b>Vinkeltolerance</b>). Overskrides pilhøjden, gennemføres handlingen ikke.")),
		],
		related: ["distribute-points", "fixed-angle", "arc-from-sagitta", "edit-line", "edit-line-part"],
	},
	{
		id: "distribute-points",
		title: t("Fordel punkter lige"),
		category: "redigering",
		summary: t("Jævn afstanden mellem en linjes indre punkter uden at ændre dens forløb."),
		keywords: [t("fordel"), t("jævn afstand"), t("distribuér"), t("spacing"), t("punkter")],
		sections: [
			S(t("Sådan gør du"), t("Fra linje- eller del-menuen: <b>Fordel punkter lige</b>.")),
			S(t("Resultat"), t("De indre punkter placeres med lige stor afstand langs det nuværende forløb. Antallet af punkter og linjens rute er uændret; kun afstanden mellem punkterne jævnes.")),
		],
		related: ["straighten-simplify", "edit-line"],
	},
	{
		id: "reshape-part",
		title: t("Omtegn en del"),
		category: "redigering",
		summary: t("Erstat et stykke af en linje med et nyt, håndtegnet forløb."),
		keywords: [t("omtegn"), t("redraw"), t("reshape"), t("gentegn"), t("erstat forløb"), t("del af linje")],
		sections: [
			S(t("Sådan gør du"), step([
				t("Vælg en sammenhængende del → <b>Omtegn del…</b>."),
				t("Tegn det nye forløb: venstreklik tilføjer punkt, højreklik afslutter. ESC annullerer."),
			])),
			S(t("Resultat"), t("Det valgte stykke erstattes af den nye kæde. Stykkets start- og slutpunkt bevares. Intet ændres, før den nye kæde er afsluttet.")),
		],
		related: ["edit-line-part", "split-line"],
	},
	{
		id: "reverse-direction",
		title: t("Linjeretning og Vend retning"),
		category: "redigering",
		summary: t("En linje har en retning fra første til sidste punkt. Den vises med små pile og kan vendes."),
		keywords: [t("retning"), t("vend retning"), t("reverse"), t("pile"), t("rækkefølge"), t("orientering")],
		sections: [
			S(t("Retningen vises"), t("Små pile langs en registreret linje peger fra dens første mod dens sidste punkt. De bliver tydeligere, når linjen er markeret.")),
			S(t("Vend retning"), t("Fra linjemenuen: <b>Vend retning</b>. Punktrækkefølgen vendes; geometrien i rummet er nøjagtig den samme, og pilene vender med det samme.")),
			S(t("Tip"), t("Retningen har betydning ved <i>Saml med anden linje</i> og for hvilken ende der regnes som start.")),
		],
		related: ["edit-line", "merge-lines", "line"],
	},
	{
		id: "top-view-shortcut",
		title: t("Se ovenfra med T"),
		category: "start",
		summary: t("Tasten T stiller kameraet lodret ned over punktskyen, så du får et tæt topbillede."),
		keywords: [t("t"), t("topvisning"), t("ovenfra"), t("top view"), t("kamera"), t("genvej"), t("tastatur")],
		sections: [
			S(t("Sådan bruges den"), t("Tryk <b>T</b> for at flytte kameraet direkte ned over punktskyen, set lige oppefra og tæt indrammet om det indlæste område. Kameraet hopper straks på plads - der er ingen animation.")),
			S(t("Vigtigt"), t("Virker ikke, mens du skriver i et tekstfelt. Genvejen ændrer kun kameraet - ingen registreringer, markeringer eller værktøjer berøres.")),
		],
		related: ["basics", "label-shortcuts"],
	},
	{
		id: "topology-check",
		title: t("Topologi-tjek"),
		category: "start",
		summary: t("Finder mulige fejl i dine registreringer - uden at ændre noget af sig selv."),
		keywords: [t("topologi"), t("tjek"), t("gap"), t("endepunkt"), t("selvskæring"), t("polygon"), t("kvalitet"), t("luk gap"), t("kontrol")],
		sections: [
			S(t("Sådan starter du det"), t("Klik på pilen øverst til højre i vieweren, vælg <b>Digitalisering → Indstillinger</b>, og klik <b>Kør tjek</b> under Topologi. Tjekket kører kun, når du selv starter det.")),
			S(t("Hvad der findes"), ul([
				t("Endepunkter, der næsten mødes, men ikke er forbundet (gaps)"),
				t("Endepunkter, der ligger oven i hinanden"),
				t("Linjestykker med længde nul"),
				t("Linjer, der skærer sig selv"),
				t("Ugyldige polygoner"),
			])),
			S(t("Vigtigt"), t("Tjekket præsenterer kun fundene. Intet ændres, før du selv klikker <b>Luk gap</b> ud for et enkelt fund.")),
		],
		related: ["connect-endpoint", "selection"],
	},
	{
		id: "label-shortcuts",
		title: t("Vis/skjul labels med tastaturet (L / Shift+L)"),
		category: "redigering",
		summary: t("To globale genvejstaster tænder og slukker for alle almindelige labels og alle resultat-labels på én gang."),
		keywords: [t("label"), t("labels"), t("genvej"), t("tastatur"), t("l"), t("shift+l"), t("vis"), t("skjul"), t("resultat-label")],
		sections: [
			S(t("L - almindelige labels"), t("Tryk <b>L</b> for at slukke alle almindelige labels (koordinater, punktnumre, GCP-navne m.m.) på samtlige registreringer. Tryk <b>L</b> igen for at tænde dem alle igen.")),
			S(t("Shift+L - resultat-labels"), t("Tryk <b>Shift + L</b> for at slukke/tænde de orange resultat-labels (fx rørdiameter, målt areal) uafhængigt af de almindelige labels.")),
			S(t("Automatisk visning"), t("L og label-knapperne betyder, at labels må vises, hvor der er plads. En måling viser sit resultat helt, som værktøjet definerer det (fx alle koter for et rør); ligger målingens eget anker uden for billedet, står resultatet ved den del af målingen, der kan ses. Når labels bliver for tætte, får det, du tegner eller redigerer, pladsen først, derefter det valgte objekts resultat, hover og øvrige valgte objekters resultater, almindelige resultater, objektnavne, delmål og hjælpeværdier, og sidst punktkoordinater (først X/Y/Z, så kun kote, så væk). Hold musen over et punkt, en kant eller et objekt for at se det kort, og vælg objektet for at få dets label forrest. Det valgte objekt og det, du tegner eller redigerer, vises altid. En label, du selv har slukket, forbliver slukket - kun hover og redigering viser den midlertidigt. Knappen i Registreringer dæmpes, når en tændt label er skjult automatisk.")),
			S(t("Fælles tilstand"), t("Labels tændt/slukket med tastaturet er den samme tilstand, som labelvisningen i øvrigt bruger - en label, du har slukket med L, forbliver slukket, til du tænder den igen.")),
			S(t("Vigtigt"), t("Virker ikke, mens du skriver i et tekstfelt (fx et objekts navn) - så genvejen ikke utilsigtet erstatter et 'l', du er ved at skrive.")),
		],
		related: ["basics", "selection"],
	},
	{
		id: "copy-coordinates-shortcut",
		title: t("Kopiér koordinater under musen (C)"),
		category: "redigering",
		summary: t("Tasten C kopierer koordinatboksens aktuelle X/Y/Z til udklipsholderen."),
		keywords: [t("c"), t("kopier"), t("kopiér"), t("koordinater"), t("udklipsholder"), t("clipboard"), t("koordinatboks"), t("genvej"), t("tastatur")],
		sections: [
			S(t("Koordinatboksen"), t("Den lille boks med X/Y/Z, der altid vises ved navigationskuben, viser punktskyens koordinat lige under musen. Den viser \"—\" på alle tre linjer, når musen ikke peger på et gyldigt punkt.")),
			S(t("Sådan bruges genvejen"), t("Tryk <b>C</b> for at kopiere boksens aktuelle X/Y/Z til udklipsholderen, klar til at sætte ind andetsteds (fx Notesblok, Excel eller Teams).")),
			S(t("Betingelser"), ul([
				t("Der skal være en indlæst punktsky."),
				t("Boksen skal vise et reelt koordinat - virker ikke, mens den viser \"—\"."),
			])),
			S(t("Vigtigt"), t("Virker ikke, mens du skriver i et tekstfelt (fx et objekts navn) - så genvejen ikke utilsigtet erstatter et 'c', du er ved at skrive.")),
		],
		related: ["basics"],
	},
	];
}
export const HELP_ARTICLES = buildArticles();

// ── model helpers (pure) ─────────────────────────────────────────────────

const CATEGORY_IDS = new Set(HELP_CATEGORIES.map((c) => c.id));
let BY_ID = new Map(HELP_ARTICLES.map((a) => [a.id, a]));

onLanguageChange(() => {
	HELP_CATEGORIES.splice(0, HELP_CATEGORIES.length, ...buildCategories());
	HELP_ARTICLES.splice(0, HELP_ARTICLES.length, ...buildArticles());
	BY_ID = new Map(HELP_ARTICLES.map((a) => [a.id, a]));
});

export function getArticle(id){
	return BY_ID.get(id) || null;
}

export function categoryLabel(id){
	const c = HELP_CATEGORIES.find((x) => x.id === id);
	return c ? c.label : id;
}

export function articlesInCategory(categoryId){
	return HELP_ARTICLES.filter((a) => a.category === categoryId);
}

// Simple local ranking search: title hit > keyword hit > summary hit >
// section-text hit. Case/diacritic-insensitive, token-AND (every query word
// must match somewhere in the article).
function fold(s){
	return String(s).toLowerCase()
		.replace(/[æ]/g, "ae").replace(/[ø]/g, "oe").replace(/[å]/g, "aa");
}
function articleText(a){
	return fold([a.title, a.summary, ...(a.keywords || []), ...a.sections.map((s) => `${s.heading || ""} ${s.html.replace(/<[^>]+>/g, " ")}`)].join(" "));
}

export function searchArticles(query){
	const q = fold(query || "").trim();
	if(!q) return [];
	const tokens = q.split(/\s+/);

	const scored = [];
	for(const a of HELP_ARTICLES){
		const title = fold(a.title);
		const keywords = (a.keywords || []).map(fold);
		const summary = fold(a.summary);
		const full = articleText(a);

		if(!tokens.every((t) => full.includes(t))) continue;

		let score = 0;
		for(const t of tokens){
			if(title === t) score += 100;
			else if(title.includes(t)) score += 40;
			if(keywords.some((k) => k === t)) score += 30;
			else if(keywords.some((k) => k.includes(t))) score += 12;
			if(summary.includes(t)) score += 6;
		}
		scored.push({article: a, score});
	}
	scored.sort((x, y) => y.score - x.score || x.article.title.localeCompare(y.article.title, getLanguage()));
	return scored.map((s) => s.article);
}

// Structural self-check - used by helpModel.test.js and callable at mount
// time. Returns { ok, errors:[…] }.
export function validateHelpModel(){
	const errors = [];
	const seen = new Set();

	for(const a of HELP_ARTICLES){
		if(!a.id || typeof a.id !== "string") errors.push(`article with missing id: ${a.title || "?"}`);
		if(seen.has(a.id)) errors.push(`duplicate article id: ${a.id}`);
		seen.add(a.id);
		if(!a.title) errors.push(`article ${a.id}: missing title`);
		if(!a.summary) errors.push(`article ${a.id}: missing summary`);
		if(!CATEGORY_IDS.has(a.category)) errors.push(`article ${a.id}: unknown category "${a.category}"`);
		if(!Array.isArray(a.sections) || a.sections.length === 0) errors.push(`article ${a.id}: no sections`);
		for(const s of a.sections || []){
			if(typeof s.html !== "string" || !s.html.trim()) errors.push(`article ${a.id}: empty section html`);
		}
		for(const r of a.related || []){
			if(!BY_ID.has(r)) errors.push(`article ${a.id}: related -> unknown id "${r}"`);
			if(r === a.id) errors.push(`article ${a.id}: related to itself`);
		}
	}
	for(const c of HELP_CATEGORIES){
		if(articlesInCategory(c.id).length === 0) errors.push(`category ${c.id}: has no articles`);
	}
	return {ok: errors.length === 0, errors};
}
